import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  PencilIcon,
  PlayIcon,
  MagnifyingGlassIcon,
  GlobeAltIcon,
  SparklesIcon,
  PlusIcon,
  TrashIcon,
  UserGroupIcon,
  ArrowPathIcon,
  FunnelIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { api } from '../services/api';
import toast from 'react-hot-toast';
import { useTheme } from '../contexts/ThemeContext';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

const PROXY_OPERATION_TIMEOUT = 600000; // 10 minutes
const BULK_PROXY_CHUNK_SIZE = 1000; // send at most 1000 proxies per request to avoid 413

const MAX_ACCOUNTS_PER_PROXY = 5;

/** Always return a string for toast/display - never pass an object to toast or React children */
function getErrorMessage(err) {
  if (err == null) return 'Request failed';
  if (typeof err === 'string') return err;
  const d = err?.response?.data?.error;
  if (typeof d === 'string') return d;
  if (d != null && typeof d === 'object' && typeof d.message === 'string') return d.message;
  if (typeof err.message === 'string') return err.message;
  return 'Request failed';
}
const PROXIES_PER_PAGE = 50;
// Map account status to short tag for checker results
function accountStatusTag(status) {
  const s = (status || '').toLowerCase();
  if (s === 'active') return 'logged-in';
  if (s === 'auth_expired') return 'auth expired';
  if (s === 'suspended' || s === 'captcha' || s === 'banned') return s;
  if (s) return s.replace(/_/g, ' ');
  return '—';
}

const FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'not_used', label: 'Not used' },
  { value: 'has_slots', label: 'Has slots' },
  { value: 'has_logged_in', label: 'Logged-in' },
  { value: 'has_suspensions', label: 'Has suspensions' },
  { value: 'auth_expired', label: 'Auth expired' },
  { value: 'has_captcha', label: 'Captcha' },
  { value: 'connection_error', label: 'Connection error (status 0)' }
];

const Proxies = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const { socket } = useSocket();
  const isAdmin = user?.role === 'admin';
  const [activeTab, setActiveTab] = useState('list'); // 'list' | 'groups' | 'checker'
  const [accounts, setAccounts] = useState([]);
  const [accountGroups, setAccountGroups] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  /** Accounts with proxy + resolved proxy_auth for Groups tab (from API) */
  const [groupTabAccounts, setGroupTabAccounts] = useState([]);
  const [groupTabLoading, setGroupTabLoading] = useState(false);
  const [groupTabSelectedGroups, setGroupTabSelectedGroups] = useState([]);
  const [authPoolStats, setAuthPoolStats] = useState([]);
  const [authPoolStatsLoading, setAuthPoolStatsLoading] = useState(false);
  const [authPoolErrorWindowHours, setAuthPoolErrorWindowHours] = useState(48);
  const [selectedPoolAuths, setSelectedPoolAuths] = useState([]);
  const [poolAuthActionLoading, setPoolAuthActionLoading] = useState(false);
  const [showPoolRenameModal, setShowPoolRenameModal] = useState(false);
  const [poolRenameToAuth, setPoolRenameToAuth] = useState('');
  const [showPoolReplaceModal, setShowPoolReplaceModal] = useState(false);
  const [poolReplaceToAuth, setPoolReplaceToAuth] = useState('');
  const [poolReplaceProxyStrings, setPoolReplaceProxyStrings] = useState('');
  const [poolReplaceDeleteExisting, setPoolReplaceDeleteExisting] = useState(true);
  const [poolReplaceReassignAccounts, setPoolReplaceReassignAccounts] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [proxyString, setProxyString] = useState('');
  const [testingProxy, setTestingProxy] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [showTestResultModal, setShowTestResultModal] = useState(false);

  // Proxy list (ISP static – max 5 accounts per proxy)
  const [proxyList, setProxyList] = useState([]);
  const [proxyListLoading, setProxyListLoading] = useState(false);
  const [proxyFilter, setProxyFilter] = useState('');
  const [proxyHostFilter, setProxyHostFilter] = useState('');
  const [proxySearchQuery, setProxySearchQuery] = useState('');
  const [proxySortBy, setProxySortBy] = useState(null); // 'accounts' | 'loggedIn' | 'suspended' | 'auth' | 'captcha' | null
  const [proxySortOrder, setProxySortOrder] = useState('desc'); // 'asc' | 'desc'
  const [proxyPage, setProxyPage] = useState(1);
  const [proxyTotal, setProxyTotal] = useState(0);
  const [proxyTotalPages, setProxyTotalPages] = useState(0);
  const [totalFreeSlots, setTotalFreeSlots] = useState(0);
  const [connectionErrorCount, setConnectionErrorCount] = useState(0);
  const [poolConnectionErrorCount, setPoolConnectionErrorCount] = useState(0);
  const [selectedProxyIds, setSelectedProxyIds] = useState([]);

  const [showAddProxyModal, setShowAddProxyModal] = useState(false);
  const [addProxyString, setAddProxyString] = useState('');
  const [addProxyMaxAccounts, setAddProxyMaxAccounts] = useState(MAX_ACCOUNTS_PER_PROXY);
  const [addProxyToPool, setAddProxyToPool] = useState(false);
  const [bulkAdding, setBulkAdding] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkUpdatingCreds, setBulkUpdatingCreds] = useState(false);
  const [selectAllProxiesLoading, setSelectAllProxiesLoading] = useState(false);
  const [showBulkCredsModal, setShowBulkCredsModal] = useState(false);
  const [bulkUsername, setBulkUsername] = useState('');
  const [bulkPassword, setBulkPassword] = useState('');
  const [autoAssigning, setAutoAssigning] = useState(false);
  const [showAutoAssignModal, setShowAutoAssignModal] = useState(false);
  const [autoAssignTarget, setAutoAssignTarget] = useState('all'); // 'all' or group name
  const [autoAssignMaxPerProxy, setAutoAssignMaxPerProxy] = useState(MAX_ACCOUNTS_PER_PROXY); // accounts per proxy when auto-assigning (min 1)
  const [balanceMaxPerProxy, setBalanceMaxPerProxy] = useState('');
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [poolGologinSyncing, setPoolGologinSyncing] = useState(false);

  // Progress for bulk-delete, bulk-add, auto-assign
  const [proxyProgress, setProxyProgress] = useState({ operation: null, current: 0, total: 0, status: null });
  const completedViaSocketRef = useRef(false);
  const addingFromGenerateRef = useRef(false);
  const autoAssignJobIdRef = useRef(null);

  // Assign modal
  const [assignProxy, setAssignProxy] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignAccountIds, setAssignAccountIds] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [accountsForAssign, setAccountsForAssign] = useState([]);
  const [assignGroupFilter, setAssignGroupFilter] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [accountsGroupFilter, setAccountsGroupFilter] = useState(''); // filter "Accounts with proxies" by group
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [accountsGroupActionLoading, setAccountsGroupActionLoading] = useState(false); // remove or auto-assign for group

  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateCount, setGenerateCount] = useState(100);
  const [generateMaxAccounts, setGenerateMaxAccounts] = useState(MAX_ACCOUNTS_PER_PROXY);
  const [generateAddToPool, setGenerateAddToPool] = useState(false);
  const [generatedProxies, setGeneratedProxies] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [generatePort, setGeneratePort] = useState(30);
  const [generateUsernamePrefix, setGenerateUsernamePrefix] = useState('26031220y8ko4w5-package-isp-session-');
  const [generateUsernameSuffix, setGenerateUsernameSuffix] = useState('-time-120');
  const [generatePassword, setGeneratePassword] = useState('');

  // Proxy checker (admin only) — ProxyChecker-style: ipify + geolocation + optional speed
  const [checkerTimeoutMs, setCheckerTimeoutMs] = useState(12000);
  const [checkerIncludeSpeed, setCheckerIncludeSpeed] = useState(false);
  const [checkerRunning, setCheckerRunning] = useState(false);
  const [checkerResults, setCheckerResults] = useState([]);
  const [checkerSummary, setCheckerSummary] = useState(null);
  const [checkerProgress, setCheckerProgress] = useState({ current: 0, total: 0, successCount: 0, failureCount: 0, status: null });
  const [checkerResultsLive, setCheckerResultsLive] = useState([]); // results as they complete (during run)
  const checkAllJobIdRef = useRef(null);
  /** Separate job id for pool-auth checker from Accounts by group tab */
  const poolAuthCheckJobIdRef = useRef(null);
  const [poolAuthCheckerRunning, setPoolAuthCheckerRunning] = useState(false);
  const [poolAuthCheckerProgress, setPoolAuthCheckerProgress] = useState({
    current: 0,
    total: 0,
    successCount: 0,
    failureCount: 0,
    status: null
  });
  const [poolAuthCheckerResults, setPoolAuthCheckerResults] = useState([]);
  const [poolAuthCheckerResultsLive, setPoolAuthCheckerResultsLive] = useState([]);
  const [poolAuthCheckerSummary, setPoolAuthCheckerSummary] = useState(null);
  // Single proxy string test (HOST:PORT:USERNAME:PASSWORD)
  const [checkerSingleProxyString, setCheckerSingleProxyString] = useState('');
  const [checkerSingleResult, setCheckerSingleResult] = useState(null);
  const [checkerTestingSingle, setCheckerTestingSingle] = useState(false);
  // Checker results: sort (latency, country, status, speed) and filter (all, ok, failed)
  const [checkerSortBy, setCheckerSortBy] = useState('latency_asc'); // latency_asc | latency_desc | country | status | speed
  const [checkerFilterStatus, setCheckerFilterStatus] = useState('all'); // all | ok | failed
  // Limit check to proxies used by accounts in these groups (empty = all proxies)
  const [checkerSelectedGroups, setCheckerSelectedGroups] = useState([]);
  // Or: check only pool proxies that have no accounts attached
  const [checkerPoolUnattachedOnly, setCheckerPoolUnattachedOnly] = useState(false);
  // Saved check runs (persisted) and currently viewed run
  const [checkerSavedRuns, setCheckerSavedRuns] = useState([]);
  const [checkerSelectedRunId, setCheckerSelectedRunId] = useState(null);
  const [checkerRunCreatedAt, setCheckerRunCreatedAt] = useState(null); // when viewing a saved run, its date

  const fetchCheckerRuns = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get('/proxies/check-runs', { params: { limit: 30 } });
      setCheckerSavedRuns(Array.isArray(res.data?.runs) ? res.data.runs : []);
    } catch {
      setCheckerSavedRuns([]);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchAccounts();
    fetchProxyList(1, '');
    fetchAccountGroups();
  }, []);

  useEffect(() => {
    if (activeTab === 'checker' && isAdmin) fetchCheckerRuns();
  }, [activeTab, isAdmin]);

  useEffect(() => {
    if (!socket) return;
    const onProgress = (p) => {
      if (p.operation === 'auto-assign' && p.jobId != null && autoAssignJobIdRef.current !== p.jobId) {
        return;
      }
      setProxyProgress({
        operation: p.operation || null,
        phase: p.phase || null,
        current: p.current ?? 0,
        total: p.total ?? 0,
        status: p.status || null,
        deleted: p.deleted,
        created: p.created,
        assigned: p.assigned,
        reassigned: p.reassigned,
        updated: p.updated,
        failed: p.failed,
        skipped: p.skipped,
        onGologinSoFar: p.onGologinSoFar,
        totalAccountsInScope: p.totalAccountsInScope,
        onGologin: p.onGologin,
        skippedNotOnGologin: p.skippedNotOnGologin,
        foundViaGologinApi: p.foundViaGologinApi,
        message: typeof p.message === 'string' ? p.message : undefined
      });
      if (p.status === 'completed') {
        completedViaSocketRef.current = true;
        if (p.operation === 'bulk-delete') {
          setBulkDeleting(false);
          setSelectedProxyIds([]);
          fetchProxyList(proxyPage, proxyFilter);
          fetchAccounts();
          if (!p.error) toast.success(p.deleted != null ? `Deleted ${p.deleted} proxy(ies)` : 'Deleted');
        } else if (p.operation === 'bulk-add') {
          setBulkAdding(false);
          if (addingFromGenerateRef.current) {
            setShowGenerateModal(false);
            setGeneratedProxies([]);
            addingFromGenerateRef.current = false;
          } else {
            setShowAddProxyModal(false);
            setAddProxyString('');
          }
          fetchProxyList(proxyPage, proxyFilter);
          fetchAccounts();
          if (!p.error && p.created != null) {
            const created = p.created || 0;
            const skipped = p.skipped || 0;
            const au = p.authUpdated != null ? p.authUpdated : 0;
            // When nothing was newly created but tags were updated on existing proxies
            // (same host:port:user, different proxy_auth), surface that explicitly instead
            // of the misleading "Added 0 proxy(ies)" message.
            if (created === 0 && au > 0) {
              toast.success(
                `Updated ${au} pool auth tag(s) on existing proxies${skipped > au ? `, ${skipped - au} unchanged` : ''}`
              );
            } else if (created === 0 && skipped > 0 && au === 0) {
              toast.success(`${skipped} unchanged (duplicates, same proxy_auth)`);
            } else if (created > 0) {
              toast.success(
                `Added ${created} proxy(ies)${skipped ? `, ${skipped} skipped` : ''}${au > 0 ? `, ${au} pool auth tag(s) updated` : ''}`
              );
            }
          }
        } else if (p.operation === 'auto-assign') {
          setAutoAssigning(false);
          setAccountsGroupActionLoading(false);
          setShowAutoAssignModal(false);
          autoAssignJobIdRef.current = null;
          fetchProxyList(proxyPage, proxyFilter);
          fetchAccounts();
          if (!p.error) toast.success(p.assigned != null ? `Auto-assigned ${p.assigned} account(s)` : 'Auto-assign completed');
          else toast.error(p.error || 'Auto-assign failed');
          setTimeout(() => setProxyProgress((prev) => (prev.operation === 'auto-assign' ? { ...prev, operation: null, current: 0, total: 0, status: null } : prev)), 2000);
        } else if (p.operation === 'balance') {
          setBalanceLoading(false);
          fetchProxyList(proxyPage, proxyFilter);
          fetchAccounts();
          if (!p.error) toast.success(typeof p.message === 'string' ? p.message : `Fixed ${p.fixedOverAssigned ?? 0} over-assigned; rebalanced ${p.rebalanced ?? 0}.`);
        } else if (p.operation === 'pool-replace-reassign') {
          // Toast comes from the HTTP replace handler; just refresh lists
          fetchProxyList(proxyPage, proxyFilter);
          fetchAccounts();
          fetchGroupTabData();
        } else if (p.operation === 'pool-gologin-sync') {
          // Toast comes from the HTTP sync-gologin handler; just refresh lists
          setPoolGologinSyncing(false);
          fetchGroupTabData();
        }
        if (p.error) toast.error(getErrorMessage(p.error));
        const clearDelay = p.operation === 'pool-gologin-sync' ? 8000 : 2000;
        setTimeout(() => { setProxyProgress({ operation: null, current: 0, total: 0, status: null }); completedViaSocketRef.current = false; }, clearDelay);
      }
    };
    socket.on('proxy-progress', onProgress);
    return () => { socket.off('proxy-progress', onProgress); };
  }, [socket, proxyPage, proxyFilter]);

  useEffect(() => {
    if (!socket || !isAdmin) return;
    const onCheckProgress = (p) => {
      const jid = p.jobId;
      if (jid === checkAllJobIdRef.current) {
        setCheckerProgress({
          current: p.current ?? 0,
          total: p.total ?? 0,
          successCount: p.successCount ?? 0,
          failureCount: p.failureCount ?? 0,
          status: p.status || null
        });
      } else if (jid === poolAuthCheckJobIdRef.current) {
        setPoolAuthCheckerProgress({
          current: p.current ?? 0,
          total: p.total ?? 0,
          successCount: p.successCount ?? 0,
          failureCount: p.failureCount ?? 0,
          status: p.status || null
        });
      }
    };
    const onCheckResult = (payload) => {
      if (!payload.result) return;
      if (payload.jobId === checkAllJobIdRef.current) {
        setCheckerResultsLive((prev) => [...prev, payload.result]);
      } else if (payload.jobId === poolAuthCheckJobIdRef.current) {
        setPoolAuthCheckerResultsLive((prev) => [...prev, payload.result]);
      }
    };
    const onCheckComplete = (p) => {
      if (p.jobId === poolAuthCheckJobIdRef.current) {
        setPoolAuthCheckerRunning(false);
        setPoolAuthCheckerProgress({ current: 0, total: 0, successCount: 0, failureCount: 0, status: null });
        poolAuthCheckJobIdRef.current = null;
        if (p.error) {
          toast.error(p.error || 'Pool proxy check failed');
          return;
        }
        if (p.runId) {
          api
            .get(`/proxies/check-runs/${p.runId}`)
            .then((res) => {
              const run = res.data;
              const rows = Array.isArray(run.results) ? run.results : [];
              setPoolAuthCheckerResults(rows);
              setPoolAuthCheckerResultsLive([]);
              setPoolAuthCheckerSummary({
                total: run.total ?? 0,
                successCount: run.successCount ?? 0,
                failureCount: run.failureCount ?? 0,
                cancelled: !!run.cancelled,
                timeoutMs: run.options?.timeoutMs,
                runCreatedAt: run.createdAt,
                proxyAuths: run.options?.proxyAuths
              });
              if (p.cancelled) {
                toast('Pool check stopped', { icon: '⏹' });
              } else if ((p.successCount ?? 0) > 0 && (p.failureCount ?? 0) === 0) {
                toast.success(`Pools: all ${p.successCount} proxy(ies) passed`);
              } else if ((p.successCount ?? 0) > 0) {
                toast.success(`Pools: ${p.successCount} ok, ${p.failureCount ?? 0} failed`);
              } else {
                toast.error('Pools: all proxies failed');
              }
            })
            .catch((err) => {
              toast.error(getErrorMessage(err) || 'Failed to load pool check results');
            });
        }
        return;
      }
      if (p.jobId !== checkAllJobIdRef.current) return;
      setCheckerRunning(false);
      setCheckerProgress({ current: 0, total: 0, successCount: 0, failureCount: 0, status: null });
      if (p.error) {
        toast.error(p.error || 'Proxy check failed');
        return;
      }
      if (p.runId) {
        setCheckerSelectedRunId(p.runId);
        setCheckerRunCreatedAt(p.runCreatedAt || null);
        fetchCheckerRuns();
        api.get(`/proxies/check-runs/${p.runId}`).then((res) => {
          const run = res.data;
          setCheckerResults(Array.isArray(run.results) ? run.results : []);
          setCheckerSummary({
            total: run.total ?? 0,
            successCount: run.successCount ?? 0,
            failureCount: run.failureCount ?? 0,
            cancelled: !!run.cancelled,
            testMethod: 'ipify+geolocation',
            timeoutMs: run.options?.timeoutMs,
            runCreatedAt: run.createdAt
          });
          if (p.cancelled) {
            toast('Check stopped', { icon: '⏹' });
          } else if ((p.successCount ?? 0) > 0 && (p.failureCount ?? 0) === 0) {
            toast.success(`All ${p.successCount} proxy(ies) passed`);
          } else if ((p.successCount ?? 0) > 0) {
            toast.success(`${p.successCount} proxy(ies) passed, ${p.failureCount ?? 0} failed`);
          } else {
            toast.error('All proxies failed the check');
          }
        }).catch((err) => {
          toast.error(getErrorMessage(err) || 'Failed to load results');
        });
      }
    };
    socket.on('proxy-check-progress', onCheckProgress);
    socket.on('proxy-check-result', onCheckResult);
    socket.on('proxy-check-complete', onCheckComplete);
    return () => {
      socket.off('proxy-check-progress', onCheckProgress);
      socket.off('proxy-check-result', onCheckResult);
      socket.off('proxy-check-complete', onCheckComplete);
    };
  }, [socket, isAdmin]);

  const fetchAccounts = async () => {
    try {
      setAccountsLoading(true);
      const response = await api.get('/accounts', { params: { proxyList: '1', withProxy: '1' } });
      const list = Array.isArray(response.data) ? response.data : (response.data?.accounts || []);
      const withProxies = list.filter(
        (a) => a.proxy && a.proxy.enabled && a.proxy.host && a.proxy.port
      );
      setAccounts(withProxies);
    } catch (error) {
      toast.error('Failed to fetch accounts');
      console.error('Error fetching accounts:', error);
    } finally {
      setAccountsLoading(false);
    }
  };

  const fetchGroupTabData = useCallback(async () => {
    try {
      setGroupTabLoading(true);
      setAuthPoolStatsLoading(true);
      const groupsParam = groupTabSelectedGroups.length > 0 ? groupTabSelectedGroups.join(',') : '';
      const [accRes, statsRes] = await Promise.all([
        api.get('/proxies/accounts-by-group', { params: groupsParam ? { groups: groupsParam } : {} }),
        api.get('/proxies/auth-stats-by-pool', { params: groupsParam ? { groups: groupsParam } : {} })
      ]);
      setGroupTabAccounts(Array.isArray(accRes.data?.accounts) ? accRes.data.accounts : []);
      setAuthPoolStats(Array.isArray(statsRes.data?.stats) ? statsRes.data.stats : []);
      if (statsRes.data?.errorWindowHours) setAuthPoolErrorWindowHours(statsRes.data.errorWindowHours);
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to load group / pool data');
      setGroupTabAccounts([]);
      setAuthPoolStats([]);
    } finally {
      setGroupTabLoading(false);
      setAuthPoolStatsLoading(false);
    }
  }, [groupTabSelectedGroups]);

  const normalizedSelectablePoolAuths = useMemo(
    () => authPoolStats.map((row) => String(row?.proxyAuth || '').trim()).filter((v) => v && v !== '__none__'),
    [authPoolStats]
  );

  const togglePoolAuthSelection = (poolAuth) => {
    const key = String(poolAuth || '').trim();
    if (!key || key === '__none__') return;
    setSelectedPoolAuths((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));
  };

  const selectAllVisiblePoolAuths = () => setSelectedPoolAuths([...normalizedSelectablePoolAuths]);

  const clearPoolAuthSelection = () => setSelectedPoolAuths([]);

  const handleBulkDeleteByPoolAuth = async () => {
    if (selectedPoolAuths.length === 0) {
      toast.error('Select at least one proxy_auth');
      return;
    }
    if (!window.confirm(`Delete proxies for ${selectedPoolAuths.length} selected proxy_auth value(s)? Attached accounts will be unlinked.`)) return;
    try {
      setPoolAuthActionLoading(true);
      const res = await api.post('/proxies/pool-auth/bulk-delete', { proxyAuths: selectedPoolAuths }, { timeout: PROXY_OPERATION_TIMEOUT });
      toast.success(`Deleted ${res.data?.deleted ?? 0} proxy(ies)`);
      setSelectedPoolAuths([]);
      fetchProxyList(proxyPage, proxyFilter, proxySortBy, proxySortOrder, proxySearchQuery, proxyHostFilter);
      fetchAccounts();
      fetchGroupTabData();
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to delete by proxy_auth');
    } finally {
      setPoolAuthActionLoading(false);
    }
  };

  const handleRenamePoolAuth = async (e) => {
    e?.preventDefault();
    if (selectedPoolAuths.length !== 1) {
      toast.error('Select exactly one proxy_auth to rename');
      return;
    }
    const fromAuth = selectedPoolAuths[0];
    const toAuth = (poolRenameToAuth || '').trim();
    if (!toAuth) {
      toast.error('New proxy_auth is required');
      return;
    }
    try {
      setPoolAuthActionLoading(true);
      const res = await api.post('/proxies/pool-auth/rename', { fromAuth, toAuth }, { timeout: PROXY_OPERATION_TIMEOUT });
      toast.success(`Renamed pool auth (${res.data?.updatedProxies ?? 0} proxy(ies) updated)`);
      setShowPoolRenameModal(false);
      setPoolRenameToAuth('');
      setSelectedPoolAuths([toAuth]);
      fetchProxyList(proxyPage, proxyFilter, proxySortBy, proxySortOrder, proxySearchQuery, proxyHostFilter);
      fetchAccounts();
      fetchGroupTabData();
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to rename proxy_auth');
    } finally {
      setPoolAuthActionLoading(false);
    }
  };

  const handleReplacePoolAuth = async (e) => {
    e?.preventDefault();
    if (selectedPoolAuths.length !== 1) {
      toast.error('Select exactly one proxy_auth to replace');
      return;
    }
    const fromAuth = selectedPoolAuths[0];
    const toAuth = (poolReplaceToAuth || '').trim() || fromAuth;
    const proxyStrings = (poolReplaceProxyStrings || '').split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    if (proxyStrings.length === 0) {
      toast.error('Enter at least one proxy line');
      return;
    }
    const poolStat = authPoolStats.find((r) => String(r?.proxyAuth || '').trim() === fromAuth);
    const accountCount = poolStat?.accountCount || 0;
    const willReassign = !!poolReplaceReassignAccounts;
    const confirmMsg = willReassign
      ? `Replace proxies for "${fromAuth}" with ${proxyStrings.length} new proxies and reassign ${accountCount} account(s) onto them? Old pool proxies will be deleted.`
      : `Replace proxies for "${fromAuth}" with ${proxyStrings.length} new proxies? (accounts will not be reassigned)`;
    if (!window.confirm(confirmMsg)) return;
    try {
      setPoolAuthActionLoading(true);
      if (willReassign && accountCount > 0) {
        setProxyProgress({
          operation: 'pool-replace-reassign',
          current: 0,
          total: accountCount,
          status: 'starting'
        });
      }
      const res = await api.post(
        '/proxies/pool-auth/replace',
        {
          fromAuth,
          toAuth,
          proxyStrings,
          deleteExisting: willReassign ? true : !!poolReplaceDeleteExisting,
          reassignAccounts: willReassign
        },
        { timeout: PROXY_OPERATION_TIMEOUT }
      );
      const reassigned = res.data?.reassigned ?? 0;
      toast.success(
        willReassign
          ? `Replaced pool "${fromAuth}" → "${toAuth}": ${res.data?.created ?? 0} proxies created, ${reassigned} account(s) reassigned`
          : `Replaced pool "${fromAuth}" -> "${toAuth}" (${res.data?.created ?? 0} created, ${res.data?.authUpdated ?? 0} updated, ${res.data?.skipped ?? 0} skipped)`
      );
      setShowPoolReplaceModal(false);
      setPoolReplaceProxyStrings('');
      setPoolReplaceToAuth('');
      setPoolReplaceReassignAccounts(true);
      setSelectedPoolAuths([toAuth]);
      setProxyProgress({ operation: null, current: 0, total: 0, status: null });
      fetchProxyList(proxyPage, proxyFilter, proxySortBy, proxySortOrder, proxySearchQuery, proxyHostFilter);
      fetchAccounts();
      fetchGroupTabData();
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to replace pool proxies');
      setProxyProgress({ operation: null, current: 0, total: 0, status: null });
    } finally {
      setPoolAuthActionLoading(false);
    }
  };

  // Pushes each selected pool's accounts' CURRENT proxy onto their existing GoLogin
  // profile (skips accounts with no profile — never creates one). Use this after
  // "Replace & reassign pool" so GoLogin catches up with the proxy swap.
  const handleSyncGologinForSelectedPools = async () => {
    if (selectedPoolAuths.length === 0) {
      toast.error('Select at least one proxy_auth');
      return;
    }
    const poolLabel = selectedPoolAuths.length === 1 ? `"${selectedPoolAuths[0]}"` : `${selectedPoolAuths.length} pools`;
    if (!window.confirm(
      `Push the current proxy to GoLogin for every account in ${poolLabel} that already has a GoLogin profile? `
      + `No new profiles will be created — accounts without one are skipped.`
    )) return;
    try {
      setPoolGologinSyncing(true);
      setProxyProgress({ operation: 'pool-gologin-sync', phase: 'checking', current: 0, total: 0, status: 'checking' });
      const res = await api.post(
        '/proxies/pool-auth/sync-gologin',
        { proxyAuths: selectedPoolAuths },
        { timeout: PROXY_OPERATION_TIMEOUT }
      );
      const { totalAccountsInScope = 0, onGologin = 0, foundViaGologinApi = 0, updated = 0, failed = 0, skippedNotOnGologin = 0 } = res.data || {};
      setProxyProgress({
        operation: 'pool-gologin-sync',
        phase: 'completed',
        current: onGologin,
        total: onGologin,
        status: 'completed',
        totalAccountsInScope,
        onGologin,
        foundViaGologinApi,
        skippedNotOnGologin,
        updated,
        failed
      });
      if (!completedViaSocketRef.current) {
        if (onGologin === 0) {
          toast.success(`No accounts in ${poolLabel} have a GoLogin profile — nothing to sync`);
        } else {
          toast.success(
            `Synced proxy to GoLogin: ${updated}/${onGologin} profile(s) updated`
            + `${failed ? `, ${failed} failed` : ''}${skippedNotOnGologin ? `, ${skippedNotOnGologin} account(s) not on GoLogin` : ''}`
          );
        }
      }
      fetchGroupTabData();
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to sync proxies to GoLogin');
      setProxyProgress({ operation: null, current: 0, total: 0, status: null });
    } finally {
      setPoolGologinSyncing(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'groups') {
      fetchGroupTabData();
    }
  }, [activeTab, groupTabSelectedGroups, fetchGroupTabData]);

  useEffect(() => {
    const allowed = new Set(normalizedSelectablePoolAuths);
    setSelectedPoolAuths((prev) => prev.filter((v) => allowed.has(v)));
  }, [normalizedSelectablePoolAuths]);

  const fetchAccountGroups = async () => {
    try {
      const res = await api.get('/accounts/groups');
      setAccountGroups(res.data?.groups || []);
    } catch {
      setAccountGroups([]);
    }
  };

  const fetchProxyList = useCallback(async (page = 1, filter = proxyFilter, sortBy = proxySortBy, sortOrder = proxySortOrder, search = proxySearchQuery, host = proxyHostFilter) => {
    try {
      setProxyListLoading(true);
      const params = { page, limit: PROXIES_PER_PAGE };
      if (filter) params.filter = filter;
      if (search && search.trim()) params.search = search.trim();
      if (host && host.trim()) params.host = host.trim();
      if (sortBy) {
        params.sortBy = sortBy === 'loggedIn' ? 'loggedin' : sortBy;
        params.sortOrder = sortOrder;
      }
      const response = await api.get('/proxies', { params });
      const data = response.data;
      setProxyList(data.proxies || []);
      setProxyTotal(data.total ?? 0);
      setProxyTotalPages(data.totalPages ?? 1);
      setTotalFreeSlots(data.totalFreeSlots ?? 0);
      setConnectionErrorCount(data.connectionErrorCount ?? 0);
      setPoolConnectionErrorCount(data.poolConnectionErrorCount ?? 0);
    } catch (error) {
      toast.error('Failed to fetch proxy list');
      console.error('Error fetching proxy list:', error);
      setProxyList([]);
      setProxyTotal(0);
      setProxyTotalPages(1);
      setTotalFreeSlots(0);
      setConnectionErrorCount(0);
      setPoolConnectionErrorCount(0);
    } finally {
      setProxyListLoading(false);
    }
  }, [proxyFilter, proxySortBy, proxySortOrder, proxySearchQuery, proxyHostFilter]);

  useEffect(() => {
    if (proxyPage >= 1) fetchProxyList(proxyPage, proxyFilter, proxySortBy, proxySortOrder, proxySearchQuery, proxyHostFilter);
  }, [proxyPage, proxyFilter, proxySortBy, proxySortOrder, proxySearchQuery, proxyHostFilter]);

  const handleFilterChange = (value) => {
    setProxyFilter(value);
    setProxyPage(1);
  };

  const handleSortColumn = (column) => {
    if (proxySortBy === column) {
      if (proxySortOrder === 'desc') {
        setProxySortOrder('asc');
      } else {
        setProxySortBy(null);
        setProxySortOrder('desc');
      }
    } else {
      setProxySortBy(column);
      setProxySortOrder('desc');
    }
    setProxyPage(1);
  };

  const clearSort = () => {
    setProxySortBy(null);
    setProxySortOrder('desc');
    setProxyPage(1);
  };

  const handleAddProxy = async (e) => {
    e.preventDefault();
    const lines = addProxyString.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    if (lines.length === 0) {
      toast.error('Enter at least one proxy string (one per line)');
      return;
    }
    const toPool = !!addProxyToPool;
    const maxAccounts = toPool ? 1 : Math.max(1, Math.min(100, parseInt(addProxyMaxAccounts, 10) || MAX_ACCOUNTS_PER_PROXY));
    const total = lines.length;
    try {
      setBulkAdding(true);
      setProxyProgress({ operation: 'bulk-add', current: 0, total, status: 'starting' });
      let totalCreated = 0;
      let totalSkipped = 0;
      let totalAuthUpdated = 0;
      let totalFailed = 0;
      const allErrors = [];
      for (let i = 0; i < lines.length; i += BULK_PROXY_CHUNK_SIZE) {
        const chunk = lines.slice(i, i + BULK_PROXY_CHUNK_SIZE);
        setProxyProgress({ operation: 'bulk-add', current: i, total, status: 'in_progress' });
        const res = await api.post('/proxies/bulk', { proxyStrings: chunk, proxyPool: toPool, maxAccounts }, { timeout: PROXY_OPERATION_TIMEOUT });
        const { created = 0, skipped = 0, authUpdated = 0, failed = 0, errors = [] } = res.data;
        totalCreated += created;
        totalSkipped += skipped;
        totalAuthUpdated += authUpdated;
        totalFailed += failed;
        if (Array.isArray(errors)) allErrors.push(...errors);
        setProxyProgress({ operation: 'bulk-add', current: Math.min(i + chunk.length, total), total, status: i + chunk.length >= lines.length ? 'completed' : 'in_progress', created: totalCreated, skipped: totalSkipped, failed: totalFailed });
      }
      setProxyProgress({ operation: 'bulk-add', current: total, total, status: 'completed', created: totalCreated, skipped: totalSkipped, failed: totalFailed });
      if (totalCreated > 0) {
        if (!completedViaSocketRef.current) {
          toast.success(
            `Added ${totalCreated} proxy(ies)${totalSkipped > 0 ? `, ${totalSkipped} skipped` : ''}${totalAuthUpdated > 0 ? `, ${totalAuthUpdated} pool auth tag(s) updated` : ''}${totalFailed > 0 ? `, ${totalFailed} failed` : ''}`
          );
        }
        setShowAddProxyModal(false);
        setAddProxyString('');
        fetchProxyList(proxyPage, proxyFilter);
        fetchAccounts();
      } else if (totalSkipped > 0 || totalAuthUpdated > 0) {
        if (!completedViaSocketRef.current) {
          toast.success(
            totalAuthUpdated > 0
              ? `${totalAuthUpdated} pool auth tag(s) updated on existing proxies${totalSkipped > totalAuthUpdated ? `, ${totalSkipped - totalAuthUpdated} unchanged` : ''}`
              : `${totalSkipped} skipped (duplicates)`
          );
        }
        setShowAddProxyModal(false);
        setAddProxyString('');
        fetchProxyList(proxyPage, proxyFilter);
        fetchAccounts();
      }
      if (totalFailed > 0 && totalCreated === 0 && totalSkipped === 0 && totalAuthUpdated === 0) {
        const firstError = allErrors[0];
        toast.error(typeof firstError === 'string' ? firstError : `${totalFailed} failed`);
      }
    } catch (error) {
      const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
      toast.error(isTimeout ? 'Request timed out. Try fewer proxies or check the list later.' : getErrorMessage(error) || 'Add proxies failed');
      console.error('Error adding proxies:', error);
    } finally {
      setBulkAdding(false);
      setTimeout(() => setProxyProgress({ operation: null, current: 0, total: 0, status: null }), 2000);
    }
  };

  const handleClearConnectionError = async (proxy) => {
    try {
      await api.patch(`/proxies/${proxy._id}/clear-connection-error`);
      toast.success('Connection error cleared; proxy can be used again');
      fetchProxyList(proxyPage, proxyFilter);
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to clear connection error');
      console.error('Error clearing connection error:', error);
    }
  };

  const handleDeleteProxy = async (proxy) => {
    if (!window.confirm(`Delete proxy ${proxy.displayString || proxy.name || proxy.host + ':' + proxy.port}? Accounts using it will be unlinked.`)) return;
    try {
      await api.delete(`/proxies/${proxy._id}`);
      toast.success('Proxy deleted');
      setSelectedProxyIds((prev) => prev.filter((id) => id !== proxy._id));
      fetchProxyList(proxyPage, proxyFilter);
      fetchAccounts();
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to delete proxy');
      console.error('Error deleting proxy:', error);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProxyIds.length === 0) {
      toast.error('Select at least one proxy');
      return;
    }
    if (!window.confirm(`Delete ${selectedProxyIds.length} proxy(ies)? Accounts using them will be unlinked.`)) return;
    try {
      setBulkDeleting(true);
      setProxyProgress({ operation: 'bulk-delete', current: 0, total: selectedProxyIds.length, status: 'starting' });
      const res = await api.post('/proxies/bulk-delete', { ids: selectedProxyIds }, { timeout: PROXY_OPERATION_TIMEOUT });
      const total = selectedProxyIds.length;
      setProxyProgress({ operation: 'bulk-delete', current: total, total, status: 'completed', deleted: res.data?.deleted });
      if (!completedViaSocketRef.current) toast.success(res.data?.deleted != null ? `Deleted ${res.data.deleted} proxy(ies)` : 'Deleted');
      setSelectedProxyIds([]);
      fetchProxyList(proxyPage, proxyFilter);
      fetchAccounts();
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Bulk delete failed');
      console.error('Error bulk deleting proxies:', error);
    } finally {
      setBulkDeleting(false);
      setTimeout(() => setProxyProgress({ operation: null, current: 0, total: 0, status: null }), 2000);
    }
  };

  const openAssignModal = async (proxy) => {
    setAssignProxy(proxy);
    setAssignAccountIds([]);
    setAssignGroupFilter('');
    setShowAssignModal(true);
    setAccountsForAssign([]);
    try {
      const [accRes] = await Promise.all([
        api.get('/accounts', { params: { proxyList: '1' } }),
        fetchAccountGroups()
      ]);
      const list = Array.isArray(accRes.data) ? accRes.data : accRes.data?.accounts || [];
      setAccountsForAssign(list);
    } catch (err) {
      toast.error('Failed to load accounts');
      setShowAssignModal(false);
    }
  };

  const assignSlotsLeft = assignProxy
    ? Math.max(0, (assignProxy.maxAccounts ?? MAX_ACCOUNTS_PER_PROXY) - (assignProxy.accountCount || 0))
    : 0;

  const accountsEligibleForAssign = useMemo(() => {
    if (!assignProxy || !accountsForAssign.length) return [];
    return accountsForAssign.filter((a) => {
      const currentProxyId = a.proxy?.proxyId?.toString?.() || a.proxy?.proxyId;
      const hasNoProxy = !a.proxy?.enabled || !currentProxyId;
      return hasNoProxy;
    });
  }, [assignProxy, accountsForAssign]);

  const accountsByGroup = useMemo(() => {
    const byGroup = new Map();
    byGroup.set('', []);
    accountGroups.forEach((g) => byGroup.set(g, []));
    accountsEligibleForAssign.forEach((a) => {
      const g = (a.group && a.group.trim()) || '';
      if (!byGroup.has(g)) byGroup.set(g, []);
      byGroup.get(g).push(a);
    });
    return byGroup;
  }, [accountsEligibleForAssign, accountGroups]);

  const filteredForAssignGroup =
    assignGroupFilter === ''
      ? accountsEligibleForAssign
      : (accountsByGroup.get(assignGroupFilter) || []);

  const toggleAssignAccount = (accountId) => {
    setAssignAccountIds((prev) => {
      const next = prev.includes(accountId) ? prev.filter((id) => id !== accountId) : [...prev, accountId];
      if (next.length > assignSlotsLeft) return prev.slice(0, assignSlotsLeft);
      return next;
    });
  };

  const selectGroupForAssign = (groupKey) => {
    const groupAccounts = accountsByGroup.get(groupKey) || [];
    const ids = groupAccounts.map((a) => a._id);
    const current = new Set(assignAccountIds);
    ids.forEach((id) => current.add(id));
    const next = Array.from(current).slice(0, assignSlotsLeft);
    setAssignAccountIds(next);
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    if (!assignProxy || assignAccountIds.length === 0) {
      toast.error('Select at least one account');
      return;
    }
    if (assignAccountIds.length > assignSlotsLeft) {
      toast.error(`This proxy can have at most ${assignSlotsLeft} more account(s).`);
      return;
    }
    try {
      setAssigning(true);
      await api.post('/proxies/assign', { proxyId: assignProxy._id, accountIds: assignAccountIds });
      toast.success(`Assigned proxy to ${assignAccountIds.length} account(s)`);
      setShowAssignModal(false);
      setAssignProxy(null);
      setAssignAccountIds([]);
      fetchProxyList(proxyPage, proxyFilter);
      fetchAccounts();
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Assign failed');
      console.error('Error assigning proxy:', error);
    } finally {
      setAssigning(false);
    }
  };

  const handleAutoAssignOpen = () => {
    setAutoAssignTarget('all');
    setShowAutoAssignModal(true);
    fetchAccountGroups();
  };

  const handleAutoAssignSubmit = async (e) => {
    e?.preventDefault();
    const jobId = `autoassign-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    autoAssignJobIdRef.current = jobId;
    setAutoAssigning(true);
    setProxyProgress({ operation: 'auto-assign', current: 0, total: 1, status: 'starting' });
    try {
      const maxPer = Math.max(1, Math.min(Number(autoAssignMaxPerProxy) || MAX_ACCOUNTS_PER_PROXY, 100));
      const body = autoAssignTarget === 'all' || !autoAssignTarget ? { maxAccountsPerProxy: maxPer, jobId } : { group: autoAssignTarget, maxAccountsPerProxy: maxPer, jobId };
      const res = await api.post('/proxies/auto-assign', body, { timeout: 30000 });
      const data = res.data || {};
      if (data.status === 'started' && data.total != null) {
        setProxyProgress({ operation: 'auto-assign', current: 0, total: data.total, status: 'in_progress' });
      }
      if (data.total === 0) {
        setAutoAssigning(false);
        setShowAutoAssignModal(false);
        autoAssignJobIdRef.current = null;
        toast.success('No accounts to assign or all proxies are full');
        fetchProxyList(proxyPage, proxyFilter);
        fetchAccounts();
        setTimeout(() => setProxyProgress({ operation: null, current: 0, total: 0, status: null }), 1000);
      }
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Auto-assign failed');
      console.error('Error auto-assigning:', error);
      setAutoAssigning(false);
      autoAssignJobIdRef.current = null;
      setTimeout(() => setProxyProgress({ operation: null, current: 0, total: 0, status: null }), 1000);
    }
  };

  const handleBalanceProxies = async (e) => {
    e.preventDefault();
    const max = balanceMaxPerProxy === '' ? 0 : Math.max(0, Math.min(100, parseInt(balanceMaxPerProxy, 10) || 0));
    try {
      setBalanceLoading(true);
      const res = await api.post('/proxies/balance', max > 0 ? { maxPerProxy: max } : {}, { timeout: 300000 });
      if (!completedViaSocketRef.current) {
        const msg = res.data?.message || `Fixed ${res.data?.fixedOverAssigned ?? 0} over-assigned; rebalanced ${res.data?.rebalanced ?? 0}.`;
        toast.success(msg);
        fetchProxyList(proxyPage, proxyFilter);
        fetchAccounts();
      }
    } catch (error) {
      toast.error(error.code === 'ECONNABORTED' ? 'Balance timed out' : (getErrorMessage(error) || 'Balance failed'));
      console.error('Balance error:', error);
    } finally {
      setBalanceLoading(false);
    }
  };

  const toggleSelectProxy = (id) => {
    setSelectedProxyIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAllProxies = () => {
    if (selectedProxyIds.length >= proxyList.length) {
      setSelectedProxyIds([]);
    } else {
      setSelectedProxyIds(proxyList.map((p) => p._id));
    }
  };

  const handleSelectAllProxies = async () => {
    if (proxyTotal === 0) return;
    try {
      setSelectAllProxiesLoading(true);
      const params = {};
      if (proxyFilter) params.filter = proxyFilter;
      if (proxySearchQuery && proxySearchQuery.trim()) params.search = proxySearchQuery.trim();
      if (proxyHostFilter && proxyHostFilter.trim()) params.host = proxyHostFilter.trim();
      const res = await api.get('/proxies/ids', { params });
      const ids = res.data?.ids || [];
      setSelectedProxyIds(ids);
      if (ids.length > 0) toast.success(`Selected ${ids.length} proxy(ies)${proxyFilter ? ' (matching filter)' : ''}`);
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to load proxy IDs');
      console.error('Error selecting all proxies:', error);
    } finally {
      setSelectAllProxiesLoading(false);
    }
  };

  const handleBulkUpdateCredentials = async (e) => {
    e?.preventDefault();
    if (selectedProxyIds.length === 0) {
      toast.error('Select at least one proxy');
      return;
    }
    const username = (bulkUsername || '').trim();
    const password = (bulkPassword || '').trim();
    if (!username || !password) {
      toast.error('Username and password are required');
      return;
    }
    try {
      setBulkUpdatingCreds(true);
      const res = await api.post('/proxies/bulk-update-credentials', {
        ids: selectedProxyIds,
        username,
        password
      }, { timeout: PROXY_OPERATION_TIMEOUT });
      toast.success(`Updated credentials for ${res.data?.updatedProxies ?? selectedProxyIds.length} proxy(ies)`);
      setShowBulkCredsModal(false);
      setBulkUsername('');
      setBulkPassword('');
      fetchProxyList(proxyPage, proxyFilter, proxySortBy, proxySortOrder, proxySearchQuery, proxyHostFilter);
      fetchAccounts();
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Bulk credential update failed');
      console.error('Error bulk updating proxy credentials:', error);
    } finally {
      setBulkUpdatingCreds(false);
    }
  };

  const handleSelectAllUnused = async () => {
    try {
      setSelectAllProxiesLoading(true);
      const res = await api.get('/proxies/ids', { params: { filter: 'not_used' } });
      const ids = res.data?.ids || [];
      setSelectedProxyIds(ids);
      setProxyFilter('not_used');
      setProxyPage(1);
      if (ids.length > 0) toast.success(`Selected ${ids.length} unused proxy(ies)`);
      else toast.success('No unused proxies');
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to load unused proxy IDs');
      console.error('Error selecting unused proxies:', error);
    } finally {
      setSelectAllProxiesLoading(false);
    }
  };

  const handleGenerateProxies = async (e) => {
    e?.preventDefault();
    const count = Math.max(1, Math.min(10000, parseInt(generateCount, 10) || 100));
    if (isAdmin) {
      const password = (generatePassword || '').trim();
      if (!password) {
        toast.error('Password is required for ipcloud proxy generation');
        return;
      }
    }
    try {
      setGenerating(true);
      const body = isAdmin
        ? {
            count,
            provider: 'ipcloud',
            port: Math.max(1, Math.min(65535, parseInt(generatePort, 10) || 30)),
            usernamePrefix: (generateUsernamePrefix || '').trim() || '26031220y8ko4w5-package-isp-session-',
            usernameSuffix: (generateUsernameSuffix || '').trim(),
            password: (generatePassword || '').trim()
          }
        : { count };
      const res = await api.post('/proxies/generate-unique', body);
      setGeneratedProxies(res.data?.proxies || []);
      if ((res.data?.proxies || []).length > 0) toast.success(`Generated ${res.data.proxies.length} unique proxy(ies)`);
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Generate failed');
      setGeneratedProxies([]);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyGenerated = () => {
    if (generatedProxies.length === 0) return;
    const text = generatedProxies.join('\n');
    navigator.clipboard.writeText(text).then(() => toast.success('Copied to clipboard')).catch(() => toast.error('Copy failed'));
  };

  const handleAddGeneratedToPool = async () => {
    if (generatedProxies.length === 0) return;
    addingFromGenerateRef.current = true;
    const toPool = !!generateAddToPool;
    const maxAccounts = Math.max(1, Math.min(100, parseInt(generateMaxAccounts, 10) || MAX_ACCOUNTS_PER_PROXY));
    const total = generatedProxies.length;
    try {
      setBulkAdding(true);
      setProxyProgress({ operation: 'bulk-add', current: 0, total, status: 'starting' });
      let totalCreated = 0;
      let totalSkipped = 0;
      let totalAuthUpdated = 0;
      let totalFailed = 0;
      for (let i = 0; i < generatedProxies.length; i += BULK_PROXY_CHUNK_SIZE) {
        const chunk = generatedProxies.slice(i, i + BULK_PROXY_CHUNK_SIZE);
        setProxyProgress({ operation: 'bulk-add', current: i, total, status: 'in_progress' });
        const res = await api.post('/proxies/bulk', { proxyStrings: chunk, proxyPool: toPool, maxAccounts }, { timeout: PROXY_OPERATION_TIMEOUT });
        const { created = 0, skipped = 0, authUpdated = 0, failed = 0 } = res.data ?? {};
        totalCreated += created;
        totalSkipped += skipped;
        totalAuthUpdated += authUpdated;
        totalFailed += failed;
        setProxyProgress({ operation: 'bulk-add', current: Math.min(i + chunk.length, total), total, status: i + chunk.length >= generatedProxies.length ? 'completed' : 'in_progress', created: totalCreated, skipped: totalSkipped, failed: totalFailed });
      }
      setProxyProgress({ operation: 'bulk-add', current: total, total, status: 'completed', created: totalCreated, skipped: totalSkipped, failed: totalFailed });
      if (!completedViaSocketRef.current) {
        toast.success(
          `Added ${totalCreated} proxy(ies)${totalSkipped ? `, ${totalSkipped} skipped` : ''}${totalAuthUpdated > 0 ? `, ${totalAuthUpdated} pool auth tag(s) updated` : ''}${totalFailed > 0 ? `, ${totalFailed} failed` : ''}`
        );
        setShowGenerateModal(false);
        setGeneratedProxies([]);
        fetchProxyList(proxyPage, proxyFilter);
        fetchAccounts();
      }
    } catch (error) {
      addingFromGenerateRef.current = false;
      const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
      toast.error(isTimeout ? 'Request timed out. Try adding fewer proxies at once or try again.' : (getErrorMessage(error) || 'Bulk add failed'));
    } finally {
      setBulkAdding(false);
      setTimeout(() => setProxyProgress({ operation: null, current: 0, total: 0, status: null }), 2000);
    }
  };

  const formatProxyString = (proxy) => {
    if (!proxy || !proxy.host || !proxy.port) return '';
    const u = proxy.username || '';
    const p = proxy.password || '';
    return u && p ? `${proxy.host}:${proxy.port}:${u}:${p}` : `${proxy.host}:${proxy.port}`;
  };

  const handleEditProxy = (account) => {
    const str =
      account.proxy?.host && account.proxy?.port
        ? `${account.proxy.host}:${account.proxy.port}:${account.proxy.username || ''}:${account.proxy.password || ''}`
        : '';
    setSelectedAccount(account);
    setProxyString(str);
    setShowEditModal(true);
  };

  const handleUpdateProxy = async (e) => {
    e.preventDefault();
    if (!selectedAccount) return;
    try {
      await api.put(`/accounts/${selectedAccount._id}`, {
        newProxy: { proxyString: proxyString }
      });
      toast.success('Proxy updated successfully');
      setShowEditModal(false);
      setSelectedAccount(null);
      setProxyString('');
      fetchAccounts();
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to update proxy');
      console.error('Error updating proxy:', error);
    }
  };

  const filteredAccounts = useMemo(() => {
    let list = accounts;
    if (accountsGroupFilter) {
      list = list.filter((a) => (a.group || '') === accountsGroupFilter);
    }
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase().trim();
    return list.filter((a) => {
      const matchAccount =
        (a.username || '').toLowerCase().includes(q) ||
        (a.displayName || '').toLowerCase().includes(q) ||
        (a.email || '').toLowerCase().includes(q);
      if (matchAccount) return true;
      const proxyStr = formatProxyString(a.proxy);
      return proxyStr && proxyStr.toLowerCase().includes(q);
    });
  }, [accounts, searchQuery, accountsGroupFilter]);

  const totalPages = Math.ceil(filteredAccounts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAccounts = filteredAccounts.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, accountsGroupFilter]);

  const removeProxyFromGroup = async () => {
    if (!accountsGroupFilter) return;
    const ids = filteredAccounts.map((a) => a._id).filter(Boolean);
    if (ids.length === 0) {
      toast.error('No accounts with proxy in this group');
      return;
    }
    setAccountsGroupActionLoading(true);
    try {
      const res = await api.post('/accounts/bulk-remove-proxy', { accountIds: ids });
      toast.success(res.data?.message || `Removed proxy from ${res.data?.modifiedCount ?? ids.length} account(s)`);
      fetchAccounts();
      fetchProxyList(proxyPage, proxyFilter);
    } catch (e) {
      toast.error(getErrorMessage(e) || 'Failed to remove proxy');
    } finally {
      setAccountsGroupActionLoading(false);
    }
  };

  const autoAssignToGroup = async (replace) => {
    if (!accountsGroupFilter) return;
    const jobId = `autoassign-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    autoAssignJobIdRef.current = jobId;
    setAccountsGroupActionLoading(true);
    setProxyProgress({ operation: 'auto-assign', current: 0, total: 1, status: 'starting' });
    try {
      if (replace) {
        const ids = filteredAccounts.map((a) => a._id).filter(Boolean);
        if (ids.length > 0) {
          await api.post('/accounts/bulk-remove-proxy', { accountIds: ids });
        }
      }
      const res = await api.post('/proxies/auto-assign', {
        group: accountsGroupFilter,
        maxAccountsPerProxy: autoAssignMaxPerProxy,
        jobId
      }, { timeout: 30000 });
      const data = res.data || {};
      if (data.status === 'started' && data.total != null) {
        setProxyProgress({ operation: 'auto-assign', current: 0, total: data.total, status: 'in_progress' });
      }
      if (data.total === 0) {
        setAccountsGroupActionLoading(false);
        autoAssignJobIdRef.current = null;
        toast.success('No accounts to assign or all proxies are full');
        fetchAccounts();
        fetchProxyList(proxyPage, proxyFilter);
        setTimeout(() => setProxyProgress({ operation: null, current: 0, total: 0, status: null }), 1000);
      }
    } catch (e) {
      toast.error(getErrorMessage(e) || 'Auto-assign failed');
      setAccountsGroupActionLoading(false);
      autoAssignJobIdRef.current = null;
      setTimeout(() => setProxyProgress({ operation: null, current: 0, total: 0, status: null }), 1000);
    }
  };

  const handleTestProxy = async (account) => {
    setTestingProxy(account._id);
    setTestResult(null);
    try {
      const response = await api.post(`/accounts/${account._id}/test-proxy`);
      setTestResult({ ...response.data, account });
      setShowTestResultModal(true);
      if (response.data.success) toast.success('Proxy test completed');
      else toast.error('Proxy test failed');
    } catch (error) {
      setTestResult({
        success: false,
        message: error.response?.data?.error || error.message || 'Proxy test failed',
        error: error.response?.data?.error || error.message,
        account,
        timestamp: new Date().toISOString()
      });
      setShowTestResultModal(true);
      toast.error('Proxy test failed');
      console.error('Error testing proxy:', error);
    } finally {
      setTestingProxy(null);
    }
  };

  // Checker results: filter and sort for display
  const checkerDisplayedResults = useMemo(() => {
    const list = checkerRunning ? checkerResultsLive : checkerResults;
    let out = [...list];
    if (checkerFilterStatus === 'ok') out = out.filter((r) => r.ok);
    if (checkerFilterStatus === 'failed') out = out.filter((r) => !r.ok);
    if (checkerSortBy === 'latency_asc') out.sort((a, b) => (a.responseTimeMs ?? 999999) - (b.responseTimeMs ?? 999999));
    if (checkerSortBy === 'latency_desc') out.sort((a, b) => (b.responseTimeMs ?? 0) - (a.responseTimeMs ?? 0));
    if (checkerSortBy === 'country') out.sort((a, b) => (a.country || '').localeCompare(b.country || ''));
    if (checkerSortBy === 'status') out.sort((a, b) => (a.ok === b.ok ? 0 : a.ok ? -1 : 1));
    if (checkerSortBy === 'speed') out.sort((a, b) => (b.speedKbps ?? 0) - (a.speedKbps ?? 0));
    return out;
  }, [checkerRunning, checkerResultsLive, checkerResults, checkerFilterStatus, checkerSortBy]);

  const runProxyChecker = async () => {
    if (!isAdmin) return;
    const timeoutVal = Math.max(5000, Math.min(30000, Number(checkerTimeoutMs) || 12000));
    const jobId = `check-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    checkAllJobIdRef.current = jobId;

    setCheckerRunning(true);
    setCheckerResults([]);
    setCheckerResultsLive([]);
    setCheckerSummary(null);
    setCheckerProgress({ current: 0, total: 0, successCount: 0, failureCount: 0, status: 'in_progress' });

    try {
      const body = { timeoutMs: timeoutVal, includeSpeed: checkerIncludeSpeed, jobId };
      if (checkerSelectedGroups.length > 0) body.groups = checkerSelectedGroups;
      if (checkerPoolUnattachedOnly) body.poolUnattachedOnly = true;
      const res = await api.post(
        '/proxies/check-all',
        body,
        { timeout: 30000 }
      );
      const data = res.data || {};
      if (data.status === 'started' && data.total != null) {
        setCheckerProgress((prev) => ({ ...prev, total: data.total }));
      }
      if (data.total === 0) {
        setCheckerRunning(false);
        setCheckerProgress({ current: 0, total: 0, successCount: 0, failureCount: 0, status: null });
        toast.error('No proxies to check');
      }
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to start proxy check');
      console.error('Error starting proxy checker:', error);
      setCheckerRunning(false);
      setCheckerProgress({ current: 0, total: 0, successCount: 0, failureCount: 0, status: null });
    }
  };


  const stopProxyChecker = async () => {
    const jobId = checkAllJobIdRef.current;
    if (!jobId || !isAdmin) return;
    try {
      await api.post('/proxies/check-all-cancel', { jobId });
    } catch (e) {
      toast.error(getErrorMessage(e) || 'Failed to stop');
    }
  };

  /** Same backend path as Proxy checker tab (ipify + geolocation), scoped to selected proxy_auth pools */
  const runPoolAuthProxyChecker = async () => {
    if (!isAdmin || selectedPoolAuths.length === 0) return;
    const timeoutVal = Math.max(5000, Math.min(30000, Number(checkerTimeoutMs) || 12000));
    const jobId = `check-pool-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    poolAuthCheckJobIdRef.current = jobId;

    setPoolAuthCheckerRunning(true);
    setPoolAuthCheckerResults([]);
    setPoolAuthCheckerResultsLive([]);
    setPoolAuthCheckerSummary(null);
    setPoolAuthCheckerProgress({ current: 0, total: 0, successCount: 0, failureCount: 0, status: 'in_progress' });

    try {
      const res = await api.post(
        '/proxies/check-all',
        {
          timeoutMs: timeoutVal,
          includeSpeed: checkerIncludeSpeed,
          jobId,
          proxyAuths: selectedPoolAuths
        },
        { timeout: 30000 }
      );
      const data = res.data || {};
      if (data.status === 'started' && data.total != null) {
        setPoolAuthCheckerProgress((prev) => ({ ...prev, total: data.total }));
      }
      if (data.total === 0) {
        setPoolAuthCheckerRunning(false);
        poolAuthCheckJobIdRef.current = null;
        setPoolAuthCheckerProgress({ current: 0, total: 0, successCount: 0, failureCount: 0, status: null });
        toast.error('No dashboard proxies found for the selected pool id(s)');
      }
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to start pool proxy check');
      setPoolAuthCheckerRunning(false);
      poolAuthCheckJobIdRef.current = null;
      setPoolAuthCheckerProgress({ current: 0, total: 0, successCount: 0, failureCount: 0, status: null });
    }
  };

  const stopPoolAuthProxyChecker = async () => {
    const jobId = poolAuthCheckJobIdRef.current;
    if (!jobId || !isAdmin) return;
    try {
      await api.post('/proxies/check-all-cancel', { jobId });
    } catch (e) {
      toast.error(getErrorMessage(e) || 'Failed to stop pool check');
    }
  };

  const poolAuthCheckerGrouped = useMemo(() => {
    const list = poolAuthCheckerRunning ? poolAuthCheckerResultsLive : poolAuthCheckerResults;
    const map = new Map();
    for (const r of list) {
      const key = r.proxyAuth != null && String(r.proxyAuth).trim() ? String(r.proxyAuth).trim() : '—';
      if (!map.has(key)) {
        map.set(key, { proxyAuth: key, items: [], ok: 0, fail: 0 });
      }
      const b = map.get(key);
      b.items.push(r);
      if (r.ok) b.ok += 1;
      else b.fail += 1;
    }
    const keys = [...map.keys()];
    const pref = selectedPoolAuths.filter((k) => map.has(k));
    const rest = keys.filter((k) => !pref.includes(k)).sort((a, b) => a.localeCompare(b));
    return [...pref, ...rest].map((k) => map.get(k));
  }, [
    poolAuthCheckerRunning,
    poolAuthCheckerResultsLive,
    poolAuthCheckerResults,
    selectedPoolAuths
  ]);

  const loadCheckerRun = async (runId) => {
    if (!runId || !isAdmin) return;
    try {
      const res = await api.get(`/proxies/check-runs/${runId}`);
      const run = res.data;
      setCheckerResults(Array.isArray(run.results) ? run.results : []);
      setCheckerSummary({
        total: run.total ?? 0,
        successCount: run.successCount ?? 0,
        failureCount: run.failureCount ?? 0,
        cancelled: !!run.cancelled,
        testMethod: 'ipify+geolocation',
        timeoutMs: run.options?.timeoutMs,
        runCreatedAt: run.createdAt
      });
      setCheckerSelectedRunId(run._id);
      setCheckerRunCreatedAt(run.createdAt || null);
    } catch (e) {
      toast.error(getErrorMessage(e) || 'Failed to load run');
    }
  };

  const runCheckerSingleTest = async () => {
    const raw = (checkerSingleProxyString || '').trim();
    if (!raw) {
      toast.error('Enter a proxy string (HOST:PORT:USERNAME:PASSWORD)');
      return;
    }
    if (!isAdmin) return;
    setCheckerTestingSingle(true);
    setCheckerSingleResult(null);
    try {
      const res = await api.post('/proxies/test-one', { proxyString: raw });
      setCheckerSingleResult(res.data || null);
      if (res.data?.ok) {
        toast.success(`OK — ${res.data.ip}${res.data.country ? `, ${res.data.country}` : ''}`);
      } else {
        toast.error(res.data?.error || 'Test failed');
      }
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Test failed');
      setCheckerSingleResult({ ok: false, error: getErrorMessage(error) });
    } finally {
      setCheckerTestingSingle(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div
        className={`relative overflow-hidden rounded-2xl ${
          isDarkMode
            ? 'bg-gradient-to-tr from-slate-900 via-blue-950 to-slate-900'
            : 'bg-gradient-to-tr from-sky-50 via-blue-100 to-sky-100'
        }`}
      >
        <div className="absolute inset-0 overflow-hidden">
          <div
            className={`absolute top-0 left-1/3 w-72 h-72 rounded-full blur-3xl ${
              isDarkMode ? 'bg-blue-600/15' : 'bg-blue-300/35'
            }`}
          />
          <div
            className={`absolute -bottom-20 -left-16 w-64 h-64 rounded-full blur-3xl ${
              isDarkMode ? 'bg-sky-600/20' : 'bg-sky-300/40'
            }`}
          />
          <div
            className={`absolute inset-0 ${isDarkMode ? 'opacity-[0.03]' : 'opacity-[0.08]'}`}
            style={{
              backgroundImage: `linear-gradient(${isDarkMode ? '#fff' : '#3b82f6'} 1px, transparent 1px), linear-gradient(90deg, ${isDarkMode ? '#fff' : '#3b82f6'} 1px, transparent 1px)`,
              backgroundSize: '40px 40px'
            }}
          />
        </div>
        <div className="relative px-8 py-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div
                className={`relative flex items-center justify-center w-16 h-16 rounded-2xl ${
                  isDarkMode
                    ? 'bg-gradient-to-br from-blue-500 to-sky-600 shadow-lg shadow-blue-500/30'
                    : 'bg-gradient-to-br from-blue-500 to-sky-500 shadow-lg shadow-blue-400/40'
                }`}
              >
                <GlobeAltIcon className="w-8 h-8 text-white" />
                <div
                  className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center ${
                    isDarkMode ? 'bg-cyan-400' : 'bg-cyan-500'
                  }`}
                >
                  <SparklesIcon className="w-3 h-3 text-white" />
                </div>
              </div>
              <div>
                <h1 className={`text-3xl font-bold tracking-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Proxies
                </h1>
                <p className={`mt-1.5 text-base ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  ISP static proxies — max accounts per proxy set per proxy (e.g. 0/1, 1/5)
                </p>
                <p
                  className={`mt-3 text-sm rounded-xl border px-3 py-2 max-w-3xl ${
                    isDarkMode
                      ? 'border-violet-800/60 bg-violet-950/25 text-violet-200'
                      : 'border-violet-200 bg-violet-50 text-violet-900'
                  }`}
                >
                  <span className="font-medium">Scraper auto-sourcing:</span> Per-username{' '}
                  <strong>skipped</strong> and <strong>added</strong> decisions (with reasons) are on{' '}
                  <Link to="/dashboard/scraper" className="underline font-medium hover:opacity-90">
                    Dashboard → Scraper
                  </Link>
                  . Open a session with sourcing enabled and expand <strong>Sourcing decisions</strong>.
                </p>
                <div
                  className={`mt-3 flex items-center flex-wrap gap-x-4 gap-y-1 text-sm ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}
                >
                  <span className="flex items-center">
                    <span className={`w-2 h-2 rounded-full mr-2 ${isDarkMode ? 'bg-emerald-400' : 'bg-emerald-500'}`} />
                    {proxyTotal} proxies
                  </span>
                  <span className="flex items-center">
                    <span className={`w-2 h-2 rounded-full mr-2 ${isDarkMode ? 'bg-cyan-400' : 'bg-cyan-500'}`} />
                    {totalFreeSlots} free slots in total
                  </span>
                  <span className="flex items-center">
                    <span className={`w-2 h-2 rounded-full mr-2 ${isDarkMode ? 'bg-amber-400' : 'bg-amber-500'}`} />
                    {accounts.length} accounts with proxy
                  </span>
                  {(connectionErrorCount > 0 || poolConnectionErrorCount > 0) && (
                    <>
                      <span className="flex items-center">
                        <span className={`w-2 h-2 rounded-full mr-2 ${isDarkMode ? 'bg-red-400' : 'bg-red-500'}`} />
                        {connectionErrorCount} with connection error (status 0)
                      </span>
                      <span className="flex items-center">
                        <span className={`w-2 h-2 rounded-full mr-2 ${isDarkMode ? 'bg-rose-400' : 'bg-rose-500'}`} />
                        {poolConnectionErrorCount} pool with connection error
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs: Proxy list / Accounts by group / Proxy checker (admin only) */}
      <div className="flex items-center border-b border-gray-200 dark:border-gray-700 mt-2">
        <button
          type="button"
          onClick={() => setActiveTab('list')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'list'
              ? isDarkMode
                ? 'border-primary-400 text-primary-300'
                : 'border-primary-500 text-primary-600'
              : isDarkMode
                ? 'border-transparent text-gray-400 hover:text-gray-200'
                : 'border-transparent text-gray-500 hover:text-gray-800'
          }`}
        >
          Proxy list
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('groups')}
          className={`ml-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'groups'
              ? isDarkMode
                ? 'border-primary-400 text-primary-300'
                : 'border-primary-500 text-primary-600'
              : isDarkMode
                ? 'border-transparent text-gray-400 hover:text-gray-200'
                : 'border-transparent text-gray-500 hover:text-gray-800'
          }`}
        >
          Accounts by group
        </button>
        {isAdmin && (
          <button
            type="button"
            onClick={() => setActiveTab('checker')}
            className={`ml-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === 'checker'
                ? isDarkMode
                  ? 'border-primary-400 text-primary-300'
                  : 'border-primary-500 text-primary-600'
                : isDarkMode
                  ? 'border-transparent text-gray-400 hover:text-gray-200'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            Proxy checker
          </button>
        )}
      </div>

      {/* Progress bar for bulk-delete, bulk-add, auto-assign, pool replace */}
      {proxyProgress.operation && (activeTab === 'list' || activeTab === 'groups') && (
        <div className={`rounded-xl border overflow-hidden ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="px-4 py-3 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {proxyProgress.operation === 'bulk-delete' && (proxyProgress.status === 'completed' ? 'Deleting proxies completed' : 'Deleting proxies…')}
              {proxyProgress.operation === 'bulk-add' && (proxyProgress.status === 'completed' ? 'Adding proxies completed' : 'Adding proxies…')}
              {proxyProgress.operation === 'auto-assign' && (proxyProgress.status === 'completed' ? 'Auto-assign completed' : 'Auto-assigning…')}
              {proxyProgress.operation === 'balance' && (proxyProgress.status === 'completed' ? 'Balance completed' : 'Balancing proxies…')}
              {proxyProgress.operation === 'pool-replace-reassign' && (proxyProgress.status === 'completed' ? 'Pool replace + reassign completed' : 'Replacing pool & reassigning accounts…')}
              {proxyProgress.operation === 'pool-gologin-sync' && (proxyProgress.status === 'completed' ? 'Sync proxy to GoLogin completed' : 'Syncing proxy to GoLogin…')}
            </span>
            {proxyProgress.total > 0 && proxyProgress.status !== 'completed' && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {proxyProgress.current} / {proxyProgress.total}
              </span>
            )}
            {proxyProgress.status === 'completed' && proxyProgress.deleted != null && (
              <span className="text-sm text-green-600 dark:text-green-400">{proxyProgress.deleted} deleted</span>
            )}
            {proxyProgress.status === 'completed' && proxyProgress.created != null && (
              <span className="text-sm text-green-600 dark:text-green-400">{proxyProgress.created} added</span>
            )}
            {proxyProgress.status === 'completed' && proxyProgress.assigned != null && (
              <span className="text-sm text-green-600 dark:text-green-400">{proxyProgress.assigned} assigned</span>
            )}
            {proxyProgress.status === 'completed' && proxyProgress.operation === 'pool-replace-reassign' && proxyProgress.reassigned != null && (
              <span className="text-sm text-green-600 dark:text-green-400">{proxyProgress.reassigned} reassigned</span>
            )}
            {proxyProgress.status === 'completed' && proxyProgress.operation === 'pool-gologin-sync' && proxyProgress.updated != null && (
              <span className="text-sm text-green-600 dark:text-green-400">
                {proxyProgress.updated} updated{proxyProgress.failed ? `, ${proxyProgress.failed} failed` : ''}
              </span>
            )}
            {proxyProgress.status === 'completed' && proxyProgress.operation === 'balance' && typeof proxyProgress.message === 'string' && (
              <span className="text-sm text-green-600 dark:text-green-400">{proxyProgress.message}</span>
            )}
          </div>
          <div className="h-2 bg-gray-100 dark:bg-gray-700 overflow-hidden">
            {proxyProgress.total > 0 ? (
              <div
                className="h-full bg-primary-500 dark:bg-primary-400 transition-all duration-300"
                style={{ width: `${proxyProgress.status === 'completed' ? 100 : Math.min(100, (proxyProgress.current / proxyProgress.total) * 100)}%` }}
              />
            ) : (
              <div className="h-full w-full bg-primary-500/70 dark:bg-primary-400/70 animate-pulse" />
            )}
          </div>
        </div>
      )}

      {/* Proxy list tab */}
      {activeTab === 'list' && (
        <div className="card">
        <div className="card-body">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
              Proxy list
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[200px] max-w-xs">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search proxies (host:port or name)..."
                  value={proxySearchQuery}
                  onChange={(e) => {
                    setProxySearchQuery(e.target.value);
                    setProxyPage(1);
                  }}
                  className="form-input py-2 pl-10 pr-3 text-sm w-full"
                />
              </div>
              <div className="relative flex-1 min-w-[180px] max-w-xs">
                <input
                  type="text"
                  placeholder="Filter by host (e.g. 154.206.240.0)"
                  value={proxyHostFilter}
                  onChange={(e) => {
                    setProxyHostFilter(e.target.value);
                    setProxyPage(1);
                  }}
                  className="form-input py-2 px-3 text-sm w-full"
                />
              </div>
              <div className="flex items-center gap-1">
                <FunnelIcon className="h-5 w-5 text-gray-500" />
                <select
                  value={proxyFilter}
                  onChange={(e) => handleFilterChange(e.target.value)}
                  className="form-input py-2 pr-8 text-sm"
                >
                  {FILTER_OPTIONS.map((opt) => (
                    <option key={opt.value || 'all'} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              {proxySortBy && (
                <button
                  type="button"
                  onClick={clearSort}
                  className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                  title="Clear sort"
                >
                  <XMarkIcon className="h-5 w-5 mr-1" />
                  Clear sort
                </button>
              )}
              {proxyTotal > 0 && (
                <>
                  <button
                    type="button"
                    onClick={handleSelectAllProxies}
                    disabled={selectAllProxiesLoading || proxyTotal === 0}
                    className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium bg-gray-500 text-white hover:bg-gray-600 disabled:opacity-50 dark:bg-gray-600 dark:hover:bg-gray-500"
                    title={proxyFilter ? 'Select all proxies matching current filter' : 'Select all proxies across all pages'}
                  >
                    {selectAllProxiesLoading ? 'Loading…' : selectedProxyIds.length === proxyTotal ? 'All selected' : `Select all (${proxyTotal})`}
                  </button>
                  <button
                    type="button"
                    onClick={handleSelectAllUnused}
                    disabled={selectAllProxiesLoading}
                    className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                    title="Select all proxies with 0 accounts (unused)"
                  >
                    Select all unused
                  </button>
                </>
              )}
              {selectedProxyIds.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => setSelectedProxyIds([])}
                    className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    Clear selection
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowBulkCredsModal(true)}
                    disabled={bulkUpdatingCreds}
                    className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    Bulk replace creds ({selectedProxyIds.length})
                  </button>
                  <button
                    type="button"
                    onClick={handleBulkDelete}
                    disabled={bulkDeleting}
                    className={`inline-flex items-center px-3 py-2 rounded-xl text-sm font-semibold text-white transition-all duration-200 disabled:opacity-50 ${
                      isDarkMode
                        ? 'bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-400 hover:to-rose-400 shadow-lg shadow-red-500/30 hover:shadow-red-500/40'
                        : 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 shadow-lg shadow-red-500/30 hover:shadow-red-500/40'
                    }`}
                  >
                    <TrashIcon className="h-5 w-5 mr-2" />
                    Delete ({selectedProxyIds.length})
                  </button>
                </>
              )}
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => setShowGenerateModal(true)}
                  className={`inline-flex items-center px-3 py-2 rounded-xl text-sm font-semibold text-white transition-all duration-200 ${
                    isDarkMode
                      ? 'bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-400 hover:to-violet-400 shadow-lg shadow-purple-500/30 hover:shadow-purple-500/40'
                      : 'bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-500 hover:to-violet-500 shadow-lg shadow-purple-500/30 hover:shadow-purple-500/40'
                  }`}
                  title="Generate ipcloud sticky-session proxies (admin only)"
                >
                  <SparklesIcon className="h-5 w-5 mr-2" />
                  Generate proxies
                </button>
              )}
              <button
                type="button"
                onClick={handleAutoAssignOpen}
                disabled={totalFreeSlots === 0}
                className={`inline-flex items-center px-3 py-2 rounded-xl text-sm font-semibold text-white transition-all duration-200 disabled:opacity-50 ${
                  isDarkMode
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 shadow-lg shadow-green-500/30 hover:shadow-green-500/40'
                    : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 shadow-lg shadow-green-500/30 hover:shadow-green-500/40'
                }`}
                title="Assign proxies that have fewer than 5 accounts to accounts without a proxy"
              >
                <ArrowPathIcon className="h-5 w-5 mr-2" />
                Auto-assign
              </button>
              <button
                type="button"
                onClick={() => setShowAddProxyModal(true)}
                className={`inline-flex items-center px-3 py-2 rounded-xl text-sm font-semibold text-white transition-all duration-200 ${
                  isDarkMode
                    ? 'bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-400 hover:to-sky-400 shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40'
                    : 'bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40'
                }`}
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Add proxy
              </button>
            </div>
          </div>

          {proxyListLoading ? (
            <div className="flex justify-center py-8">
              <div className="loading-spinner h-8 w-8"></div>
            </div>
          ) : proxyList.length === 0 && proxyTotal === 0 ? (
            <p className={`text-sm py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No proxies yet. Add proxies above (one per line).
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-2 text-left">
                        <input
                          type="checkbox"
                          checked={proxyList.length > 0 && selectedProxyIds.length >= proxyList.length}
                          onChange={toggleSelectAllProxies}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        Proxy
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase" title="Pool / tracking id (5th segment on bulk add)">
                        proxy_auth
                      </th>
                      <th
                        className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                        onClick={() => handleSortColumn('accounts')}
                        title="Sort by accounts (click: high→low, low→high, clear)"
                      >
                        <span className="inline-flex items-center gap-0.5">
                          Accounts
                          {proxySortBy === 'accounts' && (proxySortOrder === 'desc' ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronUpIcon className="h-4 w-4" />)}
                        </span>
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase" title="Account status tags">
                        Tags
                      </th>
                      <th
                        className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                        onClick={() => handleSortColumn('loggedIn')}
                        title="Sort by logged-in"
                      >
                        <span className="inline-flex items-center gap-0.5">
                          Logged-in
                          {proxySortBy === 'loggedIn' && (proxySortOrder === 'desc' ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronUpIcon className="h-4 w-4" />)}
                        </span>
                      </th>
                      <th
                        className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                        onClick={() => handleSortColumn('suspended')}
                        title="Sort by suspended"
                      >
                        <span className="inline-flex items-center gap-0.5">
                          Suspended
                          {proxySortBy === 'suspended' && (proxySortOrder === 'desc' ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronUpIcon className="h-4 w-4" />)}
                        </span>
                      </th>
                      <th
                        className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                        onClick={() => handleSortColumn('auth')}
                        title="Sort by auth"
                      >
                        <span className="inline-flex items-center gap-0.5">
                          Auth
                          {proxySortBy === 'auth' && (proxySortOrder === 'desc' ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronUpIcon className="h-4 w-4" />)}
                        </span>
                      </th>
                      <th
                        className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                        onClick={() => handleSortColumn('captcha')}
                        title="Sort by captcha"
                      >
                        <span className="inline-flex items-center gap-0.5">
                          Captcha
                          {proxySortBy === 'captcha' && (proxySortOrder === 'desc' ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronUpIcon className="h-4 w-4" />)}
                        </span>
                      </th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase" title="Success rate (requests that got a response)">
                        Success
                      </th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase" title="Total requests through this proxy">
                        Requests
                      </th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        Status
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {proxyList.map((proxy) => {
                      const count = proxy.accountCount || 0;
                      const proxyMax = proxy.maxAccounts ?? MAX_ACCOUNTS_PER_PROXY;
                      const slotsLeft = proxyMax - count;
                      const canAssign = slotsLeft > 0;
                      return (
                        <tr key={proxy._id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedProxyIds.includes(proxy._id)}
                              onChange={() => toggleSelectProxy(proxy._id)}
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 font-mono">
                            {proxy.displayString || proxy.name || `${proxy.host}:${proxy.port}`}
                          </td>
                          <td className="px-4 py-3 text-sm font-mono text-violet-700 dark:text-violet-300 break-all max-w-[10rem]">
                            {proxy.proxyAuth || '—'}
                          </td>
                          <td className="px-4 py-3 text-center text-sm text-gray-700 dark:text-gray-300">
                            <span className={count >= proxyMax ? 'text-green-600 dark:text-green-400 font-medium' : ''}>
                              {count} / {proxyMax}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {(proxy.loggedInCount ?? 0) > 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300" title="Logged-in">{(proxy.loggedInCount ?? 0)} logged-in</span>
                              )}
                              {(proxy.suspendedCount ?? 0) > 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300" title="Suspended">{(proxy.suspendedCount ?? 0)} suspended</span>
                              )}
                              {(proxy.authExpiredCount ?? 0) > 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" title="Auth expired">{(proxy.authExpiredCount ?? 0)} auth</span>
                              )}
                              {(proxy.captchaCount ?? 0) > 0 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300" title="Captcha">{(proxy.captchaCount ?? 0)} captcha</span>
                              )}
                              {count === 0 && (
                                <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>—</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center text-sm text-gray-700 dark:text-gray-300">
                            {proxy.loggedInCount ?? 0}
                          </td>
                          <td className="px-4 py-3 text-center text-sm text-red-600 dark:text-red-400">
                            {proxy.suspendedCount ?? 0}
                          </td>
                          <td className="px-4 py-3 text-center text-sm text-amber-600 dark:text-amber-400">
                            {proxy.authExpiredCount ?? 0}
                          </td>
                          <td className="px-4 py-3 text-center text-sm text-orange-600 dark:text-orange-400">
                            {proxy.captchaCount ?? 0}
                          </td>
                          <td className="px-4 py-3 text-center text-sm text-gray-700 dark:text-gray-300">
                            {(proxy.usage?.totalRequests ?? 0) > 0
                              ? `${proxy.successRate ?? 0}%`
                              : '—'}
                          </td>
                          <td className="px-4 py-3 text-center text-sm text-gray-700 dark:text-gray-300">
                            {(proxy.usage?.totalRequests ?? 0) > 0
                              ? `${proxy.usage?.successfulRequests ?? 0}/${proxy.usage?.totalRequests ?? 0}`
                              : '—'}
                          </td>
                          <td className="px-4 py-3 text-center text-sm">
                            {proxy.lastConnectionErrorAt ? (
                              <span className="text-red-600 dark:text-red-400" title="Connection error (status 0)">Connection error</span>
                            ) : (
                              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>{proxy.status || '—'}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {proxy.lastConnectionErrorAt && (
                                <button
                                  type="button"
                                  onClick={() => handleClearConnectionError(proxy)}
                                  className="inline-flex items-center px-2 py-1.5 text-sm font-medium text-amber-600 dark:text-amber-400 hover:underline"
                                  title="Clear failed and use again"
                                >
                                  Clear failed
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => openAssignModal(proxy)}
                                disabled={!canAssign}
                                className="inline-flex items-center px-2 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                                title={canAssign ? `Assign to up to ${slotsLeft} account(s)` : 'Proxy is full (5/5)'}
                              >
                                <UserGroupIcon className="h-5 w-5 mr-1" />
                                Assign
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteProxy(proxy)}
                                className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                title="Delete"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {proxyTotalPages > 1 && (
                <div className="mt-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Showing {((proxyPage - 1) * PROXIES_PER_PAGE) + 1} to{' '}
                    {Math.min(proxyPage * PROXIES_PER_PAGE, proxyTotal)} of {proxyTotal} proxies
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setProxyPage((p) => Math.max(1, p - 1))}
                      disabled={proxyPage <= 1}
                      className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Page {proxyPage} of {proxyTotalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() => setProxyPage((p) => Math.min(proxyTotalPages, p + 1))}
                      disabled={proxyPage >= proxyTotalPages}
                      className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        </div>
      )}

      {activeTab === 'groups' && (
        <div className="space-y-6">
          <div className={`card ${isDarkMode ? 'border-gray-700' : ''}`}>
            <div className="card-body space-y-4">
              <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                Filter by account group
              </h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Select one or more groups to limit the account list and pool-auth stats below. Leave all unchecked to include every group.
              </p>
              <div className="flex flex-wrap gap-2">
                {accountGroups.length === 0 ? (
                  <span className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>No groups defined yet (assign groups on accounts first).</span>
                ) : (
                  accountGroups.map((g) => {
                    const checked = groupTabSelectedGroups.includes(g);
                    return (
                      <label
                        key={g}
                        className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${
                          checked
                            ? isDarkMode
                              ? 'border-primary-500 bg-primary-950/40 text-primary-200'
                              : 'border-primary-400 bg-primary-50 text-primary-800'
                            : isDarkMode
                              ? 'border-gray-600 bg-gray-800/50 text-gray-300'
                              : 'border-gray-200 bg-white text-gray-700'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            setGroupTabSelectedGroups((prev) =>
                              prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]
                            )
                          }
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        {g}
                      </label>
                    );
                  })
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setGroupTabSelectedGroups([...accountGroups])}
                  className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Select all groups
                </button>
                <button
                  type="button"
                  onClick={() => setGroupTabSelectedGroups([])}
                  className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Clear groups
                </button>
                <button
                  type="button"
                  onClick={() => fetchGroupTabData()}
                  disabled={groupTabLoading || authPoolStatsLoading}
                  className="text-sm px-3 py-1.5 rounded-lg bg-primary-600 text-white hover:bg-primary-500 disabled:opacity-50"
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>

          <div className={`card ${isDarkMode ? 'border-gray-700' : ''}`}>
            <div className="card-body">
              <h3 className={`text-md font-semibold mb-3 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                Suspensions and accounts by <code className="text-sm">proxy_auth</code> (pool id)
              </h3>
              <div className="flex flex-wrap gap-2 mb-3">
                <button
                  type="button"
                  onClick={selectAllVisiblePoolAuths}
                  className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                  disabled={poolAuthActionLoading || normalizedSelectablePoolAuths.length === 0}
                >
                  Select all pool ids
                </button>
                <button
                  type="button"
                  onClick={clearPoolAuthSelection}
                  className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                  disabled={poolAuthActionLoading || selectedPoolAuths.length === 0}
                >
                  Clear selection
                </button>
                <button
                  type="button"
                  onClick={handleBulkDeleteByPoolAuth}
                  className="text-sm px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-500 disabled:opacity-50"
                  disabled={poolAuthActionLoading || selectedPoolAuths.length === 0}
                >
                  Delete selected pools
                </button>
                <button
                  type="button"
                  onClick={() => { setPoolRenameToAuth(''); setShowPoolRenameModal(true); }}
                  className="text-sm px-3 py-1.5 rounded-lg bg-amber-600 text-white hover:bg-amber-500 disabled:opacity-50"
                  disabled={poolAuthActionLoading || selectedPoolAuths.length !== 1}
                >
                  Rename selected pool
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPoolReplaceToAuth(selectedPoolAuths[0] || '');
                    setPoolReplaceProxyStrings('');
                    setPoolReplaceReassignAccounts(true);
                    setPoolReplaceDeleteExisting(true);
                    setShowPoolReplaceModal(true);
                  }}
                  className="text-sm px-3 py-1.5 rounded-lg bg-primary-600 text-white hover:bg-primary-500 disabled:opacity-50"
                  disabled={poolAuthActionLoading || selectedPoolAuths.length !== 1}
                >
                  Replace & reassign pool
                </button>
                <button
                  type="button"
                  onClick={handleSyncGologinForSelectedPools}
                  className="text-sm px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50"
                  disabled={poolAuthActionLoading || poolGologinSyncing || selectedPoolAuths.length === 0}
                  title="Pushes each account's current proxy onto its existing GoLogin profile (skips accounts with no profile — never creates one)"
                >
                  {poolGologinSyncing ? 'Syncing to GoLogin…' : 'Sync proxy to GoLogin'}
                </button>
                {isAdmin && (
                  <>
                    <button
                      type="button"
                      onClick={runPoolAuthProxyChecker}
                      className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50"
                      disabled={
                        poolAuthActionLoading ||
                        selectedPoolAuths.length === 0 ||
                        poolAuthCheckerRunning
                      }
                      title="Same method as Proxy checker tab (exit IP + geolocation)"
                    >
                      <PlayIcon className="h-4 w-4 shrink-0" />
                      Check proxies (selected pools)
                    </button>
                    {poolAuthCheckerRunning && (
                      <button
                        type="button"
                        onClick={stopPoolAuthProxyChecker}
                        className="text-sm px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-500"
                      >
                        Stop pool check
                      </button>
                    )}
                  </>
                )}
              </div>
              {proxyProgress.operation === 'pool-gologin-sync' && (
                <div className={`rounded-xl border p-4 mb-3 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                      {proxyProgress.phase === 'checking' && 'Checking which accounts are on GoLogin…'}
                      {proxyProgress.phase === 'syncing' && 'Updating GoLogin profiles…'}
                      {proxyProgress.phase === 'completed' && 'Sync proxy to GoLogin completed'}
                    </span>
                    {(proxyProgress.phase === 'checking' || proxyProgress.phase === 'syncing') && (
                      <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {proxyProgress.current} / {proxyProgress.total || '…'}
                      </span>
                    )}
                  </div>
                  <div className="w-full h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden mb-3">
                    {proxyProgress.total > 0 ? (
                      <div
                        className="h-full rounded-full bg-emerald-500 transition-all duration-300"
                        style={{ width: `${proxyProgress.phase === 'completed' ? 100 : Math.min(100, (100 * proxyProgress.current) / proxyProgress.total)}%` }}
                      />
                    ) : (
                      <div className="h-full w-full bg-emerald-500/70 animate-pulse" />
                    )}
                  </div>
                  <div className={`flex flex-wrap gap-x-4 gap-y-1 text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {proxyProgress.phase === 'checking' && (
                      <span>Checked {proxyProgress.current} of {proxyProgress.total} account(s) — {proxyProgress.onGologinSoFar ?? 0} on GoLogin so far</span>
                    )}
                    {(proxyProgress.phase === 'syncing' || proxyProgress.phase === 'completed') && (
                      <>
                        <span>{proxyProgress.totalAccountsInScope ?? 0} account(s) checked</span>
                        <span className={isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}>
                          {proxyProgress.onGologin ?? 0} on GoLogin
                          {proxyProgress.foundViaGologinApi ? ` (${proxyProgress.foundViaGologinApi} found via live GoLogin lookup)` : ''}
                        </span>
                        <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                          {proxyProgress.skippedNotOnGologin ?? 0} not on GoLogin — skipped
                        </span>
                        <span className={isDarkMode ? 'text-green-400' : 'text-green-600'}>{proxyProgress.updated ?? 0} updated</span>
                        {(proxyProgress.failed ?? 0) > 0 && (
                          <span className={isDarkMode ? 'text-red-400' : 'text-red-600'}>{proxyProgress.failed} failed</span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
              {isAdmin && (
                <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Pool checks use the same engine as{' '}
                  <button
                    type="button"
                    className="text-primary-600 dark:text-primary-400 underline font-medium"
                    onClick={() => setActiveTab('checker')}
                  >
                    Proxy checker
                  </button>
                  : timeout {checkerTimeoutMs} ms
                  {checkerIncludeSpeed ? ', including speed test' : ''}. Edit those options on that tab.
                </p>
              )}
              {authPoolStatsLoading ? (
                <div className="flex justify-center py-6">
                  <div className="loading-spinner h-8 w-8" />
                </div>
              ) : authPoolStats.length === 0 ? (
                <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  No pool auth tags in this filter, or no accounts with proxy. Add proxies as{' '}
                  <span className="font-mono">host:port:user:pass:proxy_auth</span> or tag duplicates via bulk add.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Select</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">proxy_auth</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Accounts</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Suspended</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Proxies</th>
                        <th
                          className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase"
                          title={`Proxies in this pool that hit a timeout, 408, or 407 error in the last ${authPoolErrorWindowHours}h — VPS scrape requests and automations both feed this.`}
                        >
                          Proxy errors ({authPoolErrorWindowHours}h)
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {authPoolStats.map((row) => {
                        const poolAuth = String(row.proxyAuth || '').trim();
                        const selectable = poolAuth && poolAuth !== '__none__';
                        const erroring = row.errorUnits ?? 0;
                        const errorDenominator = row.errorDenominator ?? row.distinctProxies ?? 0;
                        const breakdown = [
                          row.error407Count ? `${row.error407Count} × 407` : null,
                          row.error408Count ? `${row.error408Count} × 408` : null,
                          row.errorTimeoutCount ? `${row.errorTimeoutCount} × timeout` : null
                        ].filter(Boolean).join(', ');
                        return (
                        <tr key={row.proxyAuth || '_'}>
                          <td className="px-4 py-2">
                            <input
                              type="checkbox"
                              checked={selectable && selectedPoolAuths.includes(poolAuth)}
                              onChange={() => togglePoolAuthSelection(poolAuth)}
                              disabled={!selectable || poolAuthActionLoading}
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-40"
                            />
                          </td>
                          <td className="px-4 py-2 text-sm font-mono text-gray-900 dark:text-gray-100 break-all max-w-md">
                            {row.proxyAuth === '__none__' ? '— (no pool auth)' : row.proxyAuth}
                          </td>
                          <td className="px-4 py-2 text-center text-sm text-gray-700 dark:text-gray-300">{row.accountCount ?? 0}</td>
                          <td className="px-4 py-2 text-center text-sm text-red-600 dark:text-red-400">{row.suspendedCount ?? 0}</td>
                          <td className="px-4 py-2 text-center text-sm text-gray-700 dark:text-gray-300">{row.distinctProxies ?? 0}</td>
                          <td
                            className={`px-4 py-2 text-center text-sm ${erroring > 0 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-400 dark:text-gray-500'}`}
                            title={breakdown ? `Lifetime totals: ${breakdown}` : 'No proxy errors recorded on this pool'}
                          >
                            {erroring > 0 ? `${erroring} / ${errorDenominator}` : '—'}
                          </td>
                        </tr>
                      )})}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {isAdmin &&
            (poolAuthCheckerRunning ||
              poolAuthCheckerResults.length > 0 ||
              poolAuthCheckerResultsLive.length > 0 ||
              poolAuthCheckerSummary) && (
              <div className={`card ${isDarkMode ? 'border-gray-700' : ''}`}>
                <div className="card-body space-y-4">
                  <h3 className={`text-md font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                    Proxy checker results (selected <code className="text-sm">proxy_auth</code> pools)
                  </h3>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Same method as the Proxy checker tab: api.ipify.org exit IP plus geolocation (optional speed test when enabled there).
                  </p>

                  {poolAuthCheckerRunning && poolAuthCheckerProgress.total > 0 && (
                    <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                          {poolAuthCheckerProgress.current} / {poolAuthCheckerProgress.total}
                        </span>
                        <span className="text-sm">
                          <span className={isDarkMode ? 'text-green-400' : 'text-green-600'}>{poolAuthCheckerProgress.successCount} ok</span>
                          <span className="mx-2 text-gray-400">·</span>
                          <span className={isDarkMode ? 'text-red-400' : 'text-red-600'}>{poolAuthCheckerProgress.failureCount} failed</span>
                        </span>
                      </div>
                      <div className="w-full h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-blue-500 transition-all duration-300"
                          style={{
                            width: `${poolAuthCheckerProgress.total ? (100 * poolAuthCheckerProgress.current) / poolAuthCheckerProgress.total : 0}%`
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {poolAuthCheckerRunning && poolAuthCheckerProgress.total === 0 && (
                    <div className="flex items-center gap-3 py-4">
                      <div className="loading-spinner h-6 w-6" />
                      <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Starting pool check…</span>
                    </div>
                  )}

                  {poolAuthCheckerSummary && (
                    <div
                      className={`rounded-xl border px-4 py-3 text-sm ${isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                    >
                      <span>
                        {poolAuthCheckerSummary.total} proxies checked
                        {poolAuthCheckerSummary.cancelled ? ' (stopped)' : ''}
                      </span>
                      <span className={`ml-2 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>{poolAuthCheckerSummary.successCount} ok</span>
                      <span className={`ml-2 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{poolAuthCheckerSummary.failureCount} failed</span>
                      {poolAuthCheckerSummary.runCreatedAt && (
                        <span className={`ml-2 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                          · {new Date(poolAuthCheckerSummary.runCreatedAt).toLocaleString()}
                        </span>
                      )}
                    </div>
                  )}

                  {poolAuthCheckerGrouped.length > 1 && (
                    <div>
                      <p className={`text-xs font-medium mb-2 uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Summary by pool
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {poolAuthCheckerGrouped.map((b) => (
                          <span
                            key={b.proxyAuth}
                            className={`text-xs px-2 py-1.5 rounded-lg border font-mono break-all max-w-full ${isDarkMode ? 'border-gray-600 bg-gray-800/80 text-gray-200' : 'border-gray-300 bg-white text-gray-800'}`}
                          >
                            {b.proxyAuth}:{' '}
                            <span className={isDarkMode ? 'text-green-400' : 'text-green-600'}>{b.ok} ok</span>
                            {' · '}
                            <span className={isDarkMode ? 'text-red-400' : 'text-red-600'}>{b.fail} fail</span>
                            {' · '}
                            {b.items.length} total
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {poolAuthCheckerGrouped.length > 0 && (
                    <div>
                      <p className={`text-xs font-medium mb-2 uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Per proxy
                      </p>
                      <div className="grid gap-4 md:grid-cols-2">
                        {poolAuthCheckerGrouped.map((bucket) => (
                          <div
                            key={bucket.proxyAuth}
                            className={`rounded-xl border p-4 ${isDarkMode ? 'border-gray-600 bg-gray-800/40' : 'border-gray-200 bg-gray-50'}`}
                          >
                            <div className="flex flex-wrap justify-between gap-2 mb-3 pb-2 border-b border-gray-200 dark:border-gray-600">
                              <span className="font-mono text-sm font-semibold text-violet-700 dark:text-violet-300 break-all">
                                {bucket.proxyAuth}
                              </span>
                              <span className="text-xs whitespace-nowrap">
                                <span className={isDarkMode ? 'text-green-400' : 'text-green-600'}>{bucket.ok} ok</span>
                                {' · '}
                                <span className={isDarkMode ? 'text-red-400' : 'text-red-600'}>{bucket.fail} fail</span>
                                {' · '}
                                {bucket.items.length} proxies
                              </span>
                            </div>
                            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                              {bucket.items
                                .slice()
                                .sort((a, b) => `${a.host}:${a.port}`.localeCompare(`${b.host}:${b.port}`))
                                .map((r) => (
                                  <div
                                    key={r.proxyId}
                                    className={`text-xs rounded-lg border px-2 py-2 ${
                                      r.ok
                                        ? isDarkMode
                                          ? 'border-emerald-800/60 bg-emerald-950/25'
                                          : 'border-emerald-200 bg-emerald-50/90'
                                        : isDarkMode
                                          ? 'border-red-900/50 bg-red-950/20'
                                          : 'border-red-200 bg-red-50/90'
                                    }`}
                                  >
                                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono">
                                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                                        {r.host}:{r.port}
                                      </span>
                                      {r.ok ? (
                                        <span className={isDarkMode ? 'text-green-400' : 'text-green-600'}>OK</span>
                                      ) : (
                                        <span className={isDarkMode ? 'text-red-400' : 'text-red-600'}>Fail</span>
                                      )}
                                    </div>
                                    {r.ok ? (
                                      <div className={`mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        {r.ip || '—'}
                                        {r.country ? ` · ${r.country}` : ''}
                                        {r.city ? ` · ${r.city}` : ''}
                                        {r.responseTimeMs != null ? ` · ${r.responseTimeMs} ms` : ''}
                                        {r.speedKbps != null ? ` · ~${r.speedKbps} KB/s` : ''}
                                      </div>
                                    ) : (
                                      <div className={`mt-1 ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>{r.error || 'Failed'}</div>
                                    )}
                                  </div>
                                ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

          <div className={`card ${isDarkMode ? 'border-gray-700' : ''}`}>
            <div className="card-body p-0">
              <h3 className={`text-lg font-semibold px-6 py-3 border-b border-gray-200 dark:border-gray-700 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                Accounts with proxy
                {groupTabSelectedGroups.length > 0 ? ` (${groupTabSelectedGroups.length} group filter)` : ' (all groups)'}
              </h3>
              {groupTabLoading ? (
                <div className="flex justify-center py-10">
                  <div className="loading-spinner h-8 w-8" />
                </div>
              ) : groupTabAccounts.length === 0 ? (
                <p className={`text-sm px-6 py-6 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>No accounts with proxy in this filter.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Account</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Group</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Proxy</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">proxy_auth</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {groupTabAccounts.map((a) => (
                        <tr key={a._id}>
                          <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">{a.username}</td>
                          <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">{a.group || '—'}</td>
                          <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">{a.status || '—'}</td>
                          <td className="px-4 py-2 text-sm font-mono text-gray-800 dark:text-gray-200">{a.proxyDisplay || '—'}</td>
                          <td className="px-4 py-2 text-sm font-mono text-violet-700 dark:text-violet-300 break-all max-w-xs">
                            {a.proxyAuth || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Proxy checker tab (admin only) — ProxyChecker-style: IP, country, speed */}
      {activeTab === 'checker' && isAdmin && (
        <div className="card">
          <div className="card-body space-y-6">
            {/* Single proxy string test */}
            <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <h3 className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                Test single proxy
              </h3>
              <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Format: HOST:PORT:USERNAME:PASSWORD (optional 5th segment pool tag is ignored for the connection test).
              </p>
              <div className="flex flex-wrap gap-2 items-end">
                <div className="flex-1 min-w-[200px]">
                  <input
                    type="text"
                    placeholder="host:port:username:password[:proxy_auth]"
                    value={checkerSingleProxyString}
                    onChange={(e) => setCheckerSingleProxyString(e.target.value)}
                    className="form-input w-full font-mono text-sm"
                    disabled={checkerTestingSingle}
                  />
                </div>
                <button
                  type="button"
                  onClick={runCheckerSingleTest}
                  disabled={checkerTestingSingle}
                  className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 disabled:opacity-50"
                >
                  {checkerTestingSingle ? (
                    <>
                      <span className="animate-spin mr-2 inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                      Test…
                    </>
                  ) : (
                    'Test'
                  )}
                </button>
              </div>
              {checkerSingleResult != null && (
                <div className={`mt-3 p-3 rounded-lg text-sm ${checkerSingleResult.ok ? (isDarkMode ? 'bg-emerald-900/30 text-emerald-200' : 'bg-emerald-50 text-emerald-800') : (isDarkMode ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-700')}`}>
                  {checkerSingleResult.ok ? (
                    <>
                      <span className="font-medium">OK</span> — {checkerSingleResult.ip}
                      {checkerSingleResult.country && ` · ${checkerSingleResult.country}`}
                      {checkerSingleResult.city && ` · ${checkerSingleResult.city}`}
                      {checkerSingleResult.isp && ` · ${checkerSingleResult.isp}`}
                      {checkerSingleResult.responseTimeMs != null && ` · ${checkerSingleResult.responseTimeMs} ms`}
                    </>
                  ) : (
                    <span>{checkerSingleResult.error || 'Failed'}</span>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                  Proxy checker
                </h2>
                <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Get exit IP and country for each proxy (api.ipify.org + geolocation). Same proxy URL format as your automations; uses HTTP first, then curl fallback if needed.
                </p>
                <p className={`mt-1 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  Proxies to check: {proxyTotal}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end flex-wrap">
                <div>
                  <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Timeout (ms)
                  </label>
                  <input
                    type="number"
                    min={5000}
                    max={30000}
                    value={checkerTimeoutMs}
                    onChange={(e) => setCheckerTimeoutMs(Math.max(5000, Math.min(30000, Number(e.target.value) || 12000)))}
                    className="form-input w-28 text-sm"
                  />
                  <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                    5,000–30,000
                  </p>
                </div>
                <label className={`flex items-center gap-2 cursor-pointer self-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <input
                    type="checkbox"
                    checked={checkerIncludeSpeed}
                    onChange={(e) => setCheckerIncludeSpeed(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm">Include speed test (100KB download)</span>
                </label>
                {accountGroups.length > 0 && (
                  <div className="w-full sm:w-auto">
                    <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Limit to account groups
                    </label>
                    <div className={`flex flex-wrap gap-2 p-2 rounded-lg border min-h-[38px] max-h-24 overflow-y-auto ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'}`}>
                      {accountGroups.map((g) => {
                        const selected = checkerSelectedGroups.includes(g);
                        return (
                          <label key={g} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-sm cursor-pointer ${selected ? (isDarkMode ? 'bg-primary-600 text-white' : 'bg-primary-100 text-primary-800') : (isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700')}`}>
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => setCheckerSelectedGroups((prev) => (prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]))}
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 sr-only"
                            />
                            <span>{g}</span>
                          </label>
                        );
                      })}
                      {checkerSelectedGroups.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setCheckerSelectedGroups([])}
                          className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                      {checkerSelectedGroups.length === 0 ? 'All proxies' : `${checkerSelectedGroups.length} group(s) selected`}
                    </p>
                  </div>
                )}
                <label className={`flex items-center gap-2 cursor-pointer self-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} title="Proxies in the pool with no accounts assigned">
                  <input
                    type="checkbox"
                    checked={checkerPoolUnattachedOnly}
                    onChange={(e) => setCheckerPoolUnattachedOnly(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm">Pool only (unattached)</span>
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={runProxyChecker}
                    disabled={checkerRunning || proxyTotal === 0}
                    className={`inline-flex items-center justify-center px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all duration-200 disabled:opacity-50 ${
                      isDarkMode
                        ? 'bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-400 hover:to-sky-400 shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40'
                        : 'bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40'
                    }`}
                  >
                    <PlayIcon className={`h-4 w-4 mr-2 ${checkerRunning ? 'animate-spin' : ''}`} />
                    {checkerRunning ? 'Checking…' : checkerPoolUnattachedOnly && !checkerSelectedGroups.length ? 'Run check (pool unattached only)' : checkerSelectedGroups.length > 0 ? 'Run check (selected groups only)' : 'Run check on all proxies'}
                  </button>
                  {checkerRunning && (
                    <button
                      type="button"
                      onClick={stopProxyChecker}
                      className="inline-flex items-center justify-center px-4 py-2 rounded-xl text-sm font-semibold text-white bg-red-600 hover:bg-red-500"
                    >
                      Stop
                    </button>
                  )}
                </div>
              </div>
            </div>

            {checkerRunning && checkerProgress.total > 0 && (
              <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                    {checkerProgress.current} / {checkerProgress.total}
                  </span>
                  <span className="text-sm">
                    <span className={isDarkMode ? 'text-green-400' : 'text-green-600'}>{checkerProgress.successCount} ok</span>
                    <span className="mx-2 text-gray-400">·</span>
                    <span className={isDarkMode ? 'text-red-400' : 'text-red-600'}>{checkerProgress.failureCount} failed</span>
                  </span>
                </div>
                <div className="w-full h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-blue-500 transition-all duration-300"
                    style={{ width: `${checkerProgress.total ? (100 * checkerProgress.current / checkerProgress.total) : 0}%` }}
                  />
                </div>
              </div>
            )}

            {checkerSummary && (
              <div
                className={`rounded-xl border px-4 py-3 flex flex-wrap items-center gap-4 ${
                  isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="text-sm">
                  <span className={isDarkMode ? 'text-gray-200' : 'text-gray-900'}>
                    {checkerSummary.total} proxies checked
                    {checkerSummary.cancelled && ' (stopped)'}
                  </span>
                  <span className={`ml-2 text-sm ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                    {checkerSummary.successCount} ok
                  </span>
                  <span className={`ml-2 text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                    {checkerSummary.failureCount} failed
                  </span>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {checkerSummary.testMethod || 'ipify+geolocation'} · Timeout: {checkerSummary.timeoutMs}ms
                  {checkerRunCreatedAt && (
                    <> · Run: {new Date(checkerRunCreatedAt).toLocaleString()}</>
                  )}
                </div>
                {checkerSavedRuns.length > 0 && (
                  <label className="flex items-center gap-2 text-sm ml-auto">
                    Saved runs
                    <select
                      value={checkerSelectedRunId || ''}
                      onChange={(e) => {
                        const id = e.target.value;
                        if (id) loadCheckerRun(id);
                        else {
                          setCheckerSelectedRunId(null);
                          setCheckerRunCreatedAt(null);
                        }
                      }}
                      className="form-select text-sm py-1.5 pr-8 rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800 max-w-[220px]"
                    >
                      <option value="">Current results</option>
                      {checkerSavedRuns.map((run) => (
                        <option key={run._id} value={run._id}>
                          {new Date(run.createdAt).toLocaleString()} ({run.successCount ?? 0} ok, {run.failureCount ?? 0} failed)
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
            )}

            {checkerRunning && !checkerSummary && checkerProgress.total === 0 && (
              <div className="flex items-center justify-center py-10">
                <div className="flex flex-col items-center gap-3">
                  <div className="loading-spinner h-8 w-8" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Starting…
                  </p>
                </div>
              </div>
            )}

            {!checkerRunning && checkerResults.length === 0 && checkerResultsLive.length === 0 && !checkerSummary && (
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                No results yet. Click &quot;Run check on all proxies&quot; to get exit IP, country, and response time (and optional speed) for each proxy.
              </p>
            )}

            {(checkerResults.length > 0 || (checkerRunning && checkerResultsLive.length > 0)) && (
              <>
              <div className={`flex flex-wrap items-center gap-4 py-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <span className="text-sm font-medium">Results:</span>
                <label className="flex items-center gap-2 text-sm">
                  Filter
                  <select
                    value={checkerFilterStatus}
                    onChange={(e) => setCheckerFilterStatus(e.target.value)}
                    className="form-select text-sm py-1.5 pr-8 rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800"
                  >
                    <option value="all">All</option>
                    <option value="ok">OK only</option>
                    <option value="failed">Failed only</option>
                  </select>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  Sort by
                  <select
                    value={checkerSortBy}
                    onChange={(e) => setCheckerSortBy(e.target.value)}
                    className="form-select text-sm py-1.5 pr-8 rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-800"
                  >
                    <option value="latency_asc">Latency (low → high)</option>
                    <option value="latency_desc">Latency (high → low)</option>
                    <option value="country">Country</option>
                    <option value="status">Status (OK first)</option>
                    <option value="speed">Speed (KB/s)</option>
                  </select>
                </label>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Showing {checkerDisplayedResults.length} of {checkerRunning ? checkerResultsLive.length : checkerResults.length}
                  {checkerFilterStatus !== 'all' && ' (filtered)'}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        Proxy
                      </th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        Status
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        Accounts
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        IP
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        Country
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        City
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        ISP
                      </th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        Time (ms)
                      </th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        Speed (KB/s)
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        Error
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {checkerDisplayedResults.map((r) => {
                      const ok = !!r.ok;
                      return (
                        <tr key={r.proxyId}>
                          <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-gray-100">
                            {r.host}:{r.port}
                          </td>
                          <td className="px-4 py-3 text-center text-sm">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                ok
                                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                                  : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                              }`}
                            >
                              {ok ? 'OK' : 'Failed'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {(r.accounts || []).length === 0 ? (
                                <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>—</span>
                              ) : (
                                (r.accounts || []).map((acc) => {
                                  const tag = accountStatusTag(acc.status);
                                  const isLoggedIn = (acc.status || '').toLowerCase() === 'active';
                                  const isSuspended = (acc.status || '').toLowerCase() === 'suspended';
                                  const isCaptcha = (acc.status || '').toLowerCase() === 'captcha';
                                  const isAuth = (acc.status || '').toLowerCase() === 'auth_expired';
                                  const pillClass = isLoggedIn
                                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                                    : isSuspended
                                      ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                                      : isCaptcha
                                        ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300'
                                        : isAuth
                                          ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                                          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
                                  return (
                                    <span
                                      key={acc.accountId || acc.username}
                                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${pillClass}`}
                                      title={`${acc.username || ''} (${tag})`}
                                    >
                                      {acc.username || '?'} ({tag})
                                    </span>
                                  );
                                })
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm font-mono text-gray-700 dark:text-gray-300">
                            {r.ip || '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                            {r.country || '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                            {r.city || '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                            {r.isp || '—'}
                          </td>
                          <td className="px-4 py-3 text-center text-sm text-gray-700 dark:text-gray-300">
                            {r.responseTimeMs != null ? r.responseTimeMs : '—'}
                          </td>
                          <td className="px-4 py-3 text-center text-sm text-gray-700 dark:text-gray-300">
                            {r.speedKbps != null ? r.speedKbps : '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                            {r.error || ''}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Assign modal */}
      {showAssignModal && assignProxy && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => !assigning && (setShowAssignModal(false), setAssignProxy(null), setAssignAccountIds([]))}
            />
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <form onSubmit={handleAssignSubmit}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    Assign proxy to accounts
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    Proxy: <span className="font-mono text-gray-700 dark:text-gray-300">{assignProxy.displayString || assignProxy.name || `${assignProxy.host}:${assignProxy.port}`}</span>
                    {' '}({assignProxy.accountCount ?? 0}/{assignProxy.maxAccounts ?? MAX_ACCOUNTS_PER_PROXY} used — you can add up to {assignSlotsLeft} more)
                  </p>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Filter by group
                    </label>
                    <select
                      value={assignGroupFilter}
                      onChange={(e) => setAssignGroupFilter(e.target.value)}
                      className="form-input w-full"
                    >
                      <option value="">All accounts (no proxy or this proxy)</option>
                      {Array.from(accountsByGroup.keys()).filter(Boolean).map((g) => (
                        <option key={g} value={g}>
                          {g} ({accountsByGroup.get(g)?.length ?? 0})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Select accounts (max {assignSlotsLeft})
                      </span>
                      <button
                        type="button"
                        onClick={() => setAssignAccountIds([])}
                        className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        Clear
                      </button>
                    </div>
                    <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg p-2 space-y-1">
                      {filteredForAssignGroup.length === 0 ? (
                        <p className="text-sm text-gray-500">No accounts to assign</p>
                      ) : (
                        filteredForAssignGroup.map((a) => {
                          const checked = assignAccountIds.includes(a._id);
                          const atLimit = assignAccountIds.length >= assignSlotsLeft && !checked;
                          return (
                            <label
                              key={a._id}
                              className={`flex items-center gap-2 py-1.5 px-2 rounded cursor-pointer ${
                                atLimit ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={atLimit}
                                onChange={() => toggleAssignAccount(a._id)}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="text-sm text-gray-900 dark:text-gray-100">{a.username}</span>
                              {a.group && (
                                <span className="text-xs text-gray-500">({a.group})</span>
                              )}
                            </label>
                          );
                        })
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {Array.from(accountsByGroup.keys()).map((g) => (
                      <button
                        key={g || '_none'}
                        type="button"
                        onClick={() => selectGroupForAssign(g)}
                        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-500"
                      >
                        {g || 'No group'} ({accountsByGroup.get(g)?.length ?? 0})
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">
                    Selected: {assignAccountIds.length} / {assignSlotsLeft}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                  <button
                    type="submit"
                    disabled={assigning || assignAccountIds.length === 0}
                    className="btn btn-primary"
                  >
                    {assigning ? 'Assigning…' : `Assign to ${assignAccountIds.length} account(s)`}
                  </button>
                  <button
                    type="button"
                    onClick={() => !assigning && (setShowAssignModal(false), setAssignProxy(null), setAssignAccountIds([]))}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Auto-assign modal: all accounts or specific group */}
      {showAutoAssignModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => !autoAssigning && setShowAutoAssignModal(false)}
            />
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
              <form onSubmit={handleAutoAssignSubmit}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                    Auto-assign proxies
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    Assign proxies with free slots to accounts that have no proxy. Choose which accounts to consider and how many accounts per proxy (min 1).
                  </p>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Max accounts per proxy
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={100}
                      value={autoAssignMaxPerProxy}
                      onChange={(e) => setAutoAssignMaxPerProxy(Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 1)))}
                      className="form-input w-24"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Minimum 1 (1 proxy per account).</p>
                  </div>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="autoAssignTarget"
                        checked={autoAssignTarget === 'all'}
                        onChange={() => setAutoAssignTarget('all')}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">All accounts without proxy</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="autoAssignTarget"
                        checked={autoAssignTarget !== 'all'}
                        onChange={() => setAutoAssignTarget(accountGroups[0] ?? '')}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Only accounts in group:</span>
                    </label>
                    {autoAssignTarget !== 'all' && (
                      <select
                        value={accountGroups.includes(autoAssignTarget) ? autoAssignTarget : (accountGroups[0] ?? '')}
                        onChange={(e) => setAutoAssignTarget(e.target.value)}
                        className="form-input ml-6 w-full max-w-xs"
                      >
                        <option value="">Select group…</option>
                        {accountGroups.map((g) => (
                          <option key={g} value={g}>{g}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className="border-t border-gray-200 dark:border-gray-600 pt-4 mt-4">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Rebalance existing</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                      Fix over-assigned proxies (e.g. max 1 but 2+ accounts) and optionally ensure no proxy has more than X accounts.
                    </p>
                    <div className="flex flex-wrap items-end gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Max per proxy (optional)</label>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          placeholder="0 = fix only"
                          value={balanceMaxPerProxy}
                          onChange={(e) => setBalanceMaxPerProxy(e.target.value === '' ? '' : String(Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0))))}
                          className="form-input w-24"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleBalanceProxies}
                        disabled={balanceLoading || autoAssigning}
                        className="btn btn-secondary"
                      >
                        {balanceLoading ? 'Running…' : 'Balance'}
                      </button>
                    </div>
                    {balanceLoading && proxyProgress.operation === 'balance' && (
                      <div className="mt-3 p-3 rounded-lg bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {proxyProgress.status === 'completed' ? 'Balance completed' : 'Balancing proxies…'}
                        </p>
                        <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded overflow-hidden">
                          <div
                            className="h-full bg-primary-500 dark:bg-primary-400 transition-all duration-300"
                            style={{ width: `${proxyProgress.total > 0 ? (proxyProgress.status === 'completed' ? 100 : Math.min(100, (proxyProgress.current / proxyProgress.total) * 100)) : 0}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {proxyProgress.total > 0 ? `${proxyProgress.current} / ${proxyProgress.total}` : 'Starting…'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                  <button
                    type="submit"
                    disabled={autoAssigning || (autoAssignTarget !== 'all' && !autoAssignTarget)}
                    className="btn btn-primary"
                  >
                    {autoAssigning ? 'Assigning…' : 'Auto-assign'}
                  </button>
                  <button
                    type="button"
                    onClick={() => !autoAssigning && setShowAutoAssignModal(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Generate proxies modal (admin only) */}
      {showGenerateModal && isAdmin && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => !generating && !bulkAdding && setShowGenerateModal(false)} />
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Generate proxies</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  {isAdmin
                    ? 'Generate ipcloud sticky-session proxies. Host is randomized from 154.208.64.53 and 154.206.240.0. Format: HOST:PORT:USERNAME:PASSWORD. Session IDs are unique vs existing proxies in the database.'
                    : 'Generate unique proxies. Session IDs are checked against existing proxies in the database.'}
                </p>
                {isAdmin && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Port</label>
                      <input
                        type="number"
                        min={1}
                        max={65535}
                        value={generatePort}
                        onChange={(e) => setGeneratePort(Math.max(1, Math.min(65535, parseInt(e.target.value, 10) || 30)))}
                        className="form-input w-24"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password (required)</label>
                      <input
                        type="text"
                        value={generatePassword}
                        onChange={(e) => setGeneratePassword(e.target.value)}
                        placeholder="e.g. s9dvrf30zejn"
                        className="form-input w-full"
                        autoComplete="off"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Username prefix</label>
                      <input
                        type="text"
                        value={generateUsernamePrefix}
                        onChange={(e) => setGenerateUsernamePrefix(e.target.value)}
                        placeholder="26031220y8ko4w5-package-isp-session-"
                        className="form-input w-full font-mono text-sm"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Username suffix</label>
                      <input
                        type="text"
                        value={generateUsernameSuffix}
                        onChange={(e) => setGenerateUsernameSuffix(e.target.value)}
                        placeholder="-time-120"
                        className="form-input w-full font-mono text-sm"
                      />
                    </div>
                  </div>
                )}
                <form onSubmit={handleGenerateProxies} className="flex flex-wrap items-end gap-3 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount (1–10,000)</label>
                    <input
                      type="number"
                      min={1}
                      max={10000}
                      value={generateCount}
                      onChange={(e) => setGenerateCount(Math.max(1, Math.min(10000, parseInt(e.target.value, 10) || 1)))}
                      className="form-input w-28"
                    />
                  </div>
                  <button type="submit" disabled={generating} className="btn btn-primary">
                    {generating ? 'Generating…' : 'Generate'}
                  </button>
                </form>
                <div className="mb-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={generateAddToPool}
                      onChange={(e) => setGenerateAddToPool(e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Add to pool (for auto-replace on connection failure or auto-assign)</span>
                  </label>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Max accounts per proxy</label>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={generateMaxAccounts}
                    onChange={(e) => setGenerateMaxAccounts(Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 5)))}
                    className="form-input w-24"
                  />
                  <p className="text-xs text-gray-500 mt-1">Shown as 0/N on the proxy list (e.g. 0/1 or 1/5). Use 1 for pool-only.</p>
                </div>
                {generatedProxies.length > 0 && (
                  <>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{generatedProxies.length} unique proxy(ies) generated:</p>
                    <textarea
                      readOnly
                      rows={8}
                      value={generatedProxies.join('\n')}
                      className="form-input font-mono text-sm w-full"
                    />
                    {bulkAdding && proxyProgress.operation === 'bulk-add' && (
                      <div className="mt-3 p-3 rounded-lg bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {proxyProgress.status === 'completed' ? 'Adding proxies completed' : 'Adding proxies…'}
                        </p>
                        <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded overflow-hidden">
                          <div
                            className="h-full bg-primary-500 dark:bg-primary-400 transition-all duration-300"
                            style={{ width: `${proxyProgress.total > 0 ? (proxyProgress.status === 'completed' ? 100 : Math.min(100, (proxyProgress.current / proxyProgress.total) * 100)) : 0}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {proxyProgress.total > 0 ? `${proxyProgress.current} / ${proxyProgress.total}` : 'Starting…'}
                        </p>
                      </div>
                    )}
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button type="button" onClick={handleCopyGenerated} className="btn btn-secondary" disabled={bulkAdding}>
                        Copy to clipboard
                      </button>
                      <button type="button" onClick={handleAddGeneratedToPool} disabled={bulkAdding} className="btn btn-primary">
                        {bulkAdding ? 'Adding…' : 'Add proxies'}
                      </button>
                    </div>
                  </>
                )}
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button type="button" onClick={() => !generating && !bulkAdding && setShowGenerateModal(false)} className="btn btn-secondary">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showBulkCredsModal && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 text-center">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => !bulkUpdatingCreds && setShowBulkCredsModal(false)}
            />
            <div className={`inline-block align-bottom rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:align-middle w-full max-w-lg ${
              isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'
            }`}>
              <form onSubmit={handleBulkUpdateCredentials} className="p-6 space-y-4">
                <div>
                  <h3 className="text-lg font-semibold">Bulk replace proxy credentials</h3>
                  <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Updating {selectedProxyIds.length} selected proxy(ies). This also syncs assigned account proxy credentials.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Username</label>
                  <input
                    type="text"
                    value={bulkUsername}
                    onChange={(e) => setBulkUsername(e.target.value)}
                    className="form-input w-full"
                    placeholder="e.g. 26031220y8ko4w5-package-isp-session-abya57h1y-time-120"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Password</label>
                  <input
                    type="text"
                    value={bulkPassword}
                    onChange={(e) => setBulkPassword(e.target.value)}
                    className="form-input w-full"
                    placeholder="Proxy password"
                    required
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => !bulkUpdatingCreds && setShowBulkCredsModal(false)}
                    className="btn btn-secondary"
                    disabled={bulkUpdatingCreds}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={bulkUpdatingCreds}
                  >
                    {bulkUpdatingCreds ? 'Updating...' : 'Update credentials'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showPoolRenameModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => !poolAuthActionLoading && setShowPoolRenameModal(false)} />
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
              <form onSubmit={handleRenamePoolAuth}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">Rename pool auth</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Current: <span className="font-mono">{selectedPoolAuths[0] || '—'}</span>
                  </p>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">New proxy_auth</label>
                  <input
                    type="text"
                    value={poolRenameToAuth}
                    onChange={(e) => setPoolRenameToAuth(e.target.value)}
                    className="form-input w-full font-mono"
                    placeholder="new-pool-auth"
                    required
                    disabled={poolAuthActionLoading}
                  />
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button type="submit" className="btn btn-primary sm:ml-3" disabled={poolAuthActionLoading}>
                    {poolAuthActionLoading ? 'Saving…' : 'Rename'}
                  </button>
                  <button type="button" onClick={() => !poolAuthActionLoading && setShowPoolRenameModal(false)} className="btn btn-secondary mt-2 sm:mt-0" disabled={poolAuthActionLoading}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showPoolReplaceModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => !poolAuthActionLoading && setShowPoolReplaceModal(false)} />
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <form onSubmit={handleReplacePoolAuth}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">Replace proxies for pool</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    From: <span className="font-mono">{selectedPoolAuths[0] || '—'}</span>
                    {(() => {
                      const from = selectedPoolAuths[0];
                      const stat = authPoolStats.find((r) => String(r?.proxyAuth || '').trim() === from);
                      if (!stat) return null;
                      return (
                        <span className="ml-2">
                          ({stat.accountCount || 0} account{(stat.accountCount || 0) === 1 ? '' : 's'}, {stat.distinctProxies || 0} prox{(stat.distinctProxies || 0) === 1 ? 'y' : 'ies'})
                        </span>
                      );
                    })()}
                  </p>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Target proxy_auth (optional)</label>
                    <input
                      type="text"
                      value={poolReplaceToAuth}
                      onChange={(e) => setPoolReplaceToAuth(e.target.value)}
                      className="form-input w-full font-mono"
                      placeholder="leave unchanged or enter new pool id"
                      disabled={poolAuthActionLoading}
                    />
                  </div>
                  <label className="inline-flex items-start gap-2 mb-3">
                    <input
                      type="checkbox"
                      checked={poolReplaceReassignAccounts}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setPoolReplaceReassignAccounts(on);
                        if (on) setPoolReplaceDeleteExisting(true);
                      }}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-0.5"
                      disabled={poolAuthActionLoading}
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Reassign all accounts on this <span className="font-mono">proxy_auth</span> onto the new proxies
                      <span className="block text-xs text-gray-500 mt-0.5">
                        Recommended for swapping a dead provider pool. Deletes old pool proxies and spreads accounts evenly across the new list (raises max accounts per proxy if needed).
                      </span>
                    </span>
                  </label>
                  <label className={`inline-flex items-center gap-2 mb-3 ${poolReplaceReassignAccounts ? 'opacity-60' : ''}`}>
                    <input
                      type="checkbox"
                      checked={poolReplaceReassignAccounts ? true : poolReplaceDeleteExisting}
                      onChange={(e) => setPoolReplaceDeleteExisting(e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      disabled={poolAuthActionLoading || poolReplaceReassignAccounts}
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Delete existing proxies in this pool first{poolReplaceReassignAccounts ? ' (required when reassigning)' : ''}</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-2">One proxy per line: host:port:user:pass (optional 5th segment is ignored here; target pool id above is used).</p>
                  <textarea
                    value={poolReplaceProxyStrings}
                    onChange={(e) => setPoolReplaceProxyStrings(e.target.value)}
                    rows={9}
                    className="form-input w-full font-mono text-sm"
                    placeholder={"host:port:user:pass\nhost2:port2:user2:pass2"}
                    disabled={poolAuthActionLoading}
                  />
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button type="submit" className="btn btn-primary sm:ml-3" disabled={poolAuthActionLoading}>
                    {poolAuthActionLoading
                      ? (poolReplaceReassignAccounts ? 'Replacing & reassigning…' : 'Replacing…')
                      : (poolReplaceReassignAccounts ? 'Replace & reassign accounts' : 'Replace proxies')}
                  </button>
                  <button type="button" onClick={() => !poolAuthActionLoading && setShowPoolReplaceModal(false)} className="btn btn-secondary mt-2 sm:mt-0" disabled={poolAuthActionLoading}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Add proxy modal (one per line, supports single or bulk) */}
      {showAddProxyModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => !bulkAdding && (setShowAddProxyModal(false), setAddProxyString(''))}
            />
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleAddProxy}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Add proxy</h3>
                  <div className="mb-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={addProxyToPool}
                        onChange={(e) => setAddProxyToPool(e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Add to pool (1 account per proxy, auto-replace on connection failure)</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1 ml-6">Pool proxies are used to replace failed proxies when connection error (status 0) occurs.</p>
                  </div>
                  {!addProxyToPool && (
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Max accounts per proxy (1–100)</label>
                      <input
                        type="number"
                        min={1}
                        max={100}
                        value={addProxyMaxAccounts}
                        onChange={(e) => setAddProxyMaxAccounts(Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 5)))}
                        className="form-input w-24"
                      />
                      <p className="text-xs text-gray-500 mt-1">Shown as 0/N on the proxy list; accounts can be assigned up to this limit.</p>
                    </div>
                  )}
                  <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    One proxy per line. Format: host:port:username:password — optional 5th segment{' '}
                    <code className="text-xs">proxy_auth</code> for pool tracking. Re-uploading the
                    same <span className="font-mono">host:port:user:pass</span> with a different 5th
                    segment <em>only</em> updates the <code className="text-xs">proxy_auth</code> on
                    the existing proxy and syncs linked accounts (no duplicate is created).
                  </p>
                  <textarea
                    value={addProxyString}
                    onChange={(e) => setAddProxyString(e.target.value)}
                    rows={10}
                    className="form-input mt-1 w-full font-mono text-sm"
                    placeholder={"host:port:user:pass\nhost:port:user:pass:my-pool-tag"}
                    disabled={bulkAdding}
                  />
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button type="submit" className="btn btn-primary sm:ml-3" disabled={bulkAdding}>
                    {bulkAdding ? 'Adding…' : 'Add'}
                  </button>
                  <button
                    type="button"
                    onClick={() => !bulkAdding && (setShowAddProxyModal(false), setAddProxyString(''))}
                    className="btn btn-secondary mt-2 sm:mt-0"
                    disabled={bulkAdding}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Search + Group filter + Accounts list (accounts with proxies) */}
      {accounts.length > 0 && (
        <div className="mb-4 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search by account username, display name, email, or proxy (e.g. host:port)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="text-sm border border-gray-300 dark:border-gray-600 rounded-md pl-10 pr-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 w-full"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 shrink-0">
              <span>Group</span>
              <select
                value={accountsGroupFilter}
                onChange={(e) => setAccountsGroupFilter(e.target.value)}
                className="form-select text-sm py-2 rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 min-w-[140px]"
              >
                <option value="">All</option>
                {accountGroups.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </label>
          </div>
          {accountsGroupFilter && (
            <div className={`flex flex-wrap items-center gap-3 p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Group &quot;{accountsGroupFilter}&quot;: {filteredAccounts.length} account(s) with proxy
              </span>
              <button
                type="button"
                onClick={removeProxyFromGroup}
                disabled={accountsGroupActionLoading || filteredAccounts.length === 0}
                className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 disabled:opacity-50"
              >
                {accountsGroupActionLoading ? '…' : 'Remove proxy from group'}
              </button>
              <button
                type="button"
                onClick={() => autoAssignToGroup(false)}
                disabled={accountsGroupActionLoading}
                className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 disabled:opacity-50"
              >
                {accountsGroupActionLoading ? '…' : 'Auto-assign (add to accounts without proxy)'}
              </button>
              <button
                type="button"
                onClick={() => autoAssignToGroup(true)}
                disabled={accountsGroupActionLoading}
                className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 disabled:opacity-50"
              >
                {accountsGroupActionLoading ? '…' : 'Auto-assign (replace: remove then assign)'}
              </button>
            </div>
          )}
          {searchQuery && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Found {filteredAccounts.length} account(s) matching &quot;{searchQuery}&quot;
            </p>
          )}
        </div>
      )}

      {filteredAccounts.length > 0 ? (
        <div className="card">
          <div className="card-body p-0">
            <h3 className={`text-lg font-semibold px-6 py-3 border-b border-gray-200 dark:border-gray-700 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
              Accounts with proxies
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Account</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Group</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Proxy</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedAccounts.map((account) => (
                    <tr key={account._id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{account.username}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{account.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {account.group || '—'}
                      </td>
                      <td className="px-6 py-4 text-sm font-mono text-gray-900 dark:text-gray-100">
                        {formatProxyString(account.proxy)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button type="button" onClick={() => handleEditProxy(account)} className="btn btn-secondary btn-sm" title="Edit Proxy">
                            <PencilIcon className="h-4 w-4 mr-1" />
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleTestProxy(account)}
                            className={`inline-flex items-center px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all duration-200 disabled:opacity-50 ${
                              isDarkMode
                                ? 'bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-400 hover:to-sky-400 shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40'
                                : 'bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-500 hover:to-sky-500 shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40'
                            }`}
                            disabled={testingProxy === account._id}
                            title="Test Proxy"
                          >
                            <PlayIcon className={`h-4 w-4 mr-1 ${testingProxy === account._id ? 'animate-spin' : ''}`} />
                            {testingProxy === account._id ? 'Testing...' : 'Test'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredAccounts.length > itemsPerPage && (
              <div className="mt-6 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4 px-6 pb-4">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredAccounts.length)} of {filteredAccounts.length} accounts
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : accounts.length > 0 ? (
        <div className="text-center py-12">
          <p className="text-sm text-gray-500 dark:text-gray-400">No accounts match &quot;{searchQuery}&quot;.</p>
          <button onClick={() => setSearchQuery('')} className="mt-4 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400">
            Clear search
          </button>
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-sm text-gray-500 dark:text-gray-400">No accounts with proxies yet. Use Assign on a proxy to attach accounts.</p>
        </div>
      )}

      {/* Test result modal */}
      {showTestResultModal && testResult && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => { setShowTestResultModal(false); setTestResult(null); }} />
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Proxy test result</h3>
                  <button onClick={() => { setShowTestResultModal(false); setTestResult(null); }} className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
                <div className={`p-4 rounded-2xl ${testResult.success ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
                  <p className={`text-sm ${testResult.success ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}`}>{testResult.message}</p>
                </div>
                {testResult.account && (
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Account: {testResult.account.username}</p>
                )}
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button type="button" onClick={() => { setShowTestResultModal(false); setTestResult(null); }} className="btn btn-primary">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit proxy modal (per account) */}
      {showEditModal && selectedAccount && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => { setShowEditModal(false); setSelectedAccount(null); setProxyString(''); }} />
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleUpdateProxy}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Edit proxy for {selectedAccount.username}</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Proxy string</label>
                    <input
                      type="text"
                      value={proxyString}
                      onChange={(e) => setProxyString(e.target.value)}
                      className="form-input mt-1 w-full"
                      placeholder="host:port:username:password[:proxy_auth]"
                      required
                    />
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button type="submit" className="btn btn-primary sm:ml-3">Update</button>
                  <button type="button" onClick={() => { setShowEditModal(false); setSelectedAccount(null); setProxyString(''); }} className="btn btn-secondary mt-2 sm:mt-0">Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Proxies;
